import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { isActiveProject } from '../lib/projectStatus';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Link2, Unlink, Calendar, ListTodo, Save, X, AlertTriangle, CheckCircle2, Clock, Info, Sparkles, Check, Trash2, Settings, ArrowRight, Pencil, AlertCircle, RefreshCw, Lock, GripVertical, ArrowUpDown, Filter, Eye, Plus, Download, FileSpreadsheet, ClipboardCheck, Share2 } from 'lucide-react';
import GanttShareDialog from '@/components/GanttShareDialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

// Import CSS separately
import '../styles/gantt.css';
import { formatDate } from '@/utils/dateFormat';

const API = '/api';

/**
 * P1 FEATURE: Gantt Chart with Task Dependencies
 */

// Status color mapping
const statusColors = {
  not_started: { bg: '#f3f4f6', fill: '#9ca3af' },
  in_progress: { bg: '#dbeafe', fill: '#3b82f6' },
  completed: { bg: '#d1fae5', fill: '#10b981' },
  completed_with_snags: { bg: '#e0f2fe', fill: '#38bdf8' },
  delayed: { bg: '#fee2e2', fill: '#ef4444' },
  awaiting_client_approval: { bg: '#fef3c7', fill: '#f59e0b' },
};

export default function GanttChart() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ganttRef = useRef(null);
  const ganttInstance = useRef(null);
  
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('Week');
  const [selectedProject, setSelectedProject] = useState(searchParams.get('project') || 'all');
  // iter-338k: Gantt share dialog
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  // iter-288: Per-BOQ task filter. When a specific project is selected, we
  // fetch its BOQ worksheets and allow the user to narrow tasks down to just
  // those converted from a single BOQ worksheet.
  const [boqWorksheets, setBoqWorksheets] = useState([]);
  const [selectedBoqWorksheet, setSelectedBoqWorksheet] = useState('all');
  // iter-289: When a BOQ is selected, build a Set of task IDs referenced by
  // that worksheet's items (via task_id / source_task_id). We filter tasks by
  // this set OR by task.boq_worksheet_id — whichever matches — because BOQs
  // built from estimates don't always back-link to their source tasks.
  const [boqTaskIds, setBoqTaskIds] = useState(() => new Set());
  
  // Dependency management - use refs for Gantt callback access
  const [dependencyMode, setDependencyMode] = useState(false);
  const [dependencySource, setDependencySource] = useState(null);
  const dependencyModeRef = useRef(false);
  const dependencySourceRef = useRef(null);
  const [showDependencyDialog, setShowDependencyDialog] = useState(false);
  const [pendingDependency, setPendingDependency] = useState({ from: null, to: null });
  const [selectedDependencyType, setSelectedDependencyType] = useState('FS'); // P1: Dependency type (FS, SS, SF, FF)
  const [showDependencyManager, setShowDependencyManager] = useState(false);
  const [selectedTaskForDep, setSelectedTaskForDep] = useState(null);
  const [editingDependency, setEditingDependency] = useState(null); // For editing existing dependencies
  
  // AI Suggestions (P2)
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [loadingAI, setLoadingAI] = useState(false);
  
  // AI Pro - Project Timeline Prediction
  const [aiProPrediction, setAiProPrediction] = useState(null);
  const [showAiProPanel, setShowAiProPanel] = useState(false);
  const [loadingAiPro, setLoadingAiPro] = useState(false);
  
  // Drag Validation & Cascade (P0-P2)
  const [showDragValidation, setShowDragValidation] = useState(false);
  const [dragValidation, setDragValidation] = useState(null);
  const [pendingDrag, setPendingDrag] = useState(null);
  const [applyingCascade, setApplyingCascade] = useState(false);
  
  // Task date editing state
  const [showDateEditor, setShowDateEditor] = useState(false);
  const [editingTaskDates, setEditingTaskDates] = useState(null);
  const [dateEditStart, setDateEditStart] = useState('');
  const [dateEditEnd, setDateEditEnd] = useState('');
  const [savingDates, setSavingDates] = useState(false);
  
  // Task Reordering & Sorting
  const [showReorderMode, setShowReorderMode] = useState(false);
  const [taskOrder, setTaskOrder] = useState([]); // Custom display order
  const [sortBy, setSortBy] = useState('start_date'); // start_date, dependency, category, vendor, custom
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  
  // Ghost Preview State (P0 Enhancement)
  const [showGhostPreview, setShowGhostPreview] = useState(false);
  const ghostPreviewRef = useRef(null);
  
  // Quick Create Task Dialog State (P0 NEW)
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [quickCreateData, setQuickCreateData] = useState({
    title: '',
    project_id: '',
    main_category_id: '',
    sub_category_id: '',
    start_date: '',
    deadline: '',
    description: '',
  });
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [mainCatSearch, setMainCatSearch] = useState('');
  const [subCatSearch, setSubCatSearch] = useState('');
  
  // iter-286: Gantt legend now supports MULTI-select. Empty Set = show all
  // tasks; otherwise filter visible tasks to only those whose main_category_id
  // is in the Set. Also surfaces a "Selected Total" so the user can see the
  // combined value of the picked categories at a glance.
  const [ganttCategoryFilter, setGanttCategoryFilter] = useState(() => new Set());
  
  // User role
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = ['owner', 'admin', 'staff'].includes(user?.role);
  const isVendor = user?.role === 'vendor';
  const isClient = user?.role === 'client';

  // Keep refs in sync with state
  useEffect(() => {
    dependencyModeRef.current = dependencyMode;
    dependencySourceRef.current = dependencySource;
  }, [dependencyMode, dependencySource]);

  useEffect(() => {
    fetchData();
  }, [selectedProject]);

  // iter-288: Fetch BOQ worksheets for the selected project so we can
  // expose a "Select BOQ" filter next to the Project dropdown.
  useEffect(() => {
    const loadWorksheets = async () => {
      if (selectedProject === 'all') {
        setBoqWorksheets([]);
        setSelectedBoqWorksheet('all');
        return;
      }
      try {
        const res = await axios.get(`${API}/boq-worksheets?project_id=${selectedProject}`);
        setBoqWorksheets(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to load BOQ worksheets for Gantt filter', err);
        setBoqWorksheets([]);
      }
      // Reset selection when project changes
      setSelectedBoqWorksheet('all');
    };
    loadWorksheets();
  }, [selectedProject]);

  // iter-289: When a BOQ is selected, fetch its items and collect task_ids
  // referenced. The Gantt filter then unions that Set with tasks whose
  // boq_worksheet_id matches.
  useEffect(() => {
    const loadBoqTasks = async () => {
      if (selectedBoqWorksheet === 'all') {
        setBoqTaskIds(new Set());
        return;
      }
      try {
        const res = await axios.get(`${API}/boq-worksheets/${selectedBoqWorksheet}`);
        const items = res.data?.items || [];
        const ids = new Set();
        items.forEach(it => {
          if (it.task_id) ids.add(it.task_id);
          if (it.source_task_id) ids.add(it.source_task_id);
        });
        setBoqTaskIds(ids);
      } catch (err) {
        console.error('Failed to load BOQ worksheet items for Gantt filter', err);
        setBoqTaskIds(new Set());
      }
    };
    loadBoqTasks();
  }, [selectedBoqWorksheet]);

  useEffect(() => {
    if (!loading && tasks.length > 0 && ganttRef.current) {
      initGantt();
    }
  }, [loading, tasks, viewMode, ganttCategoryFilter, selectedBoqWorksheet, boqTaskIds]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tasksRes, projectsRes] = await Promise.all([
        axios.get(`${API}/tasks${selectedProject !== 'all' ? `?project_id=${selectedProject}` : ''}`),
        axios.get(`${API}/projects`),
      ]);
      
      let filteredTasks = tasksRes.data;
      let filteredProjects = projectsRes.data;
      
      // Role-based filtering for Vendor/Client views
      if (isVendor) {
        // Vendors see only tasks assigned to them or from their projects
        filteredTasks = tasksRes.data.filter(t => 
          t.assigned_to === user?.id || 
          t.assigned_vendors?.includes(user?.id)
        );
        const vendorProjectIds = [...new Set(filteredTasks.map(t => t.project_id))];
        filteredProjects = projectsRes.data.filter(p => vendorProjectIds.includes(p.id));
      } else if (isClient) {
        // Clients see only tasks from their projects
        const clientProjects = projectsRes.data.filter(p => p.client_id === user?.id);
        const clientProjectIds = clientProjects.map(p => p.id);
        filteredTasks = tasksRes.data.filter(t => clientProjectIds.includes(t.project_id));
        filteredProjects = clientProjects;
      }
      
      // Fix 2: Gantt shows only active projects
      filteredProjects = filteredProjects.filter(isActiveProject);
      const activeIds = new Set(filteredProjects.map(p => p.id));
      filteredTasks = filteredTasks.filter(t => !t.project_id || activeIds.has(t.project_id));
      setTasks(filteredTasks);
      setProjects(filteredProjects);
      
      // Initialize task order if not set
      if (taskOrder.length === 0 && filteredTasks.length > 0) {
        setTaskOrder(filteredTasks.map(t => t.id));
      }
    } catch (error) {
      toast.error('Failed to load tasks');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const initGantt = async () => {
    // Dynamically import Gantt to avoid SSR issues
    // v0.6.1 uses UMD format
    const GanttModule = await import('frappe-gantt');
    const Gantt = GanttModule.default || GanttModule;
    
    // Clear previous instance
    if (ganttRef.current) {
      ganttRef.current.innerHTML = '';
    }
    
    // Use sorted + filtered tasks for display
    const sortedTasks = getSortedTasks().filter(t => {
      // iter-289: BOQ filter — match either via task.boq_worksheet_id OR via
      // the BOQ's items[].task_id / source_task_id list.
      if (selectedBoqWorksheet !== 'all') {
        const matchesByLink = t.boq_worksheet_id === selectedBoqWorksheet;
        const matchesByItems = boqTaskIds.has(t.id);
        if (!matchesByLink && !matchesByItems) return false;
      }
      if (ganttCategoryFilter.size === 0) return true;
      return ganttCategoryFilter.has(t.category_name || t.category_code);
    });
    
    // Build category-to-color index map
    const categoryColorMap = {};
    uniqueCategories.forEach((cat, i) => {
      categoryColorMap[cat.name] = i;
    });

    // Convert tasks to Gantt format
    const ganttTasks = sortedTasks
      .filter(task => task.start_date || task.deadline)
      .map(task => {
        const start = task.start_date ? new Date(task.start_date) : new Date();
        const end = task.deadline ? new Date(task.deadline) : new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
        
        // Get project name for display
        const project = projects.find(p => p.id === task.project_id);
        
        // Format dates for display on bar (DD-MM)
        const startDateStr = formatDate(start).slice(0, 5);
        const endDateStr = formatDate(end).slice(0, 5);
        
        // Include dates in the task name for bar display
        const categoryTag = task.category_name || task.category_code || '';
        const barLabel = categoryTag
          ? `[${categoryTag}] ${task.title} (${startDateStr} - ${endDateStr})`
          : `${task.title} (${startDateStr} - ${endDateStr})`;
        
        // Auto-detect delayed: if deadline passed and task not completed
        let effectiveStatus = task.status || 'not_started';
        if (end < new Date() && !['completed', 'completed_with_snags', 'cancelled'].includes(effectiveStatus)) {
          effectiveStatus = 'delayed';
        }

        // Category color index for the bar indicator
        const catIdx = categoryTag ? (categoryColorMap[categoryTag] ?? -1) : -1;
        
        return {
          id: task.id,
          name: barLabel,
          start: formatDate(start),
          end: formatDate(end),
          progress: task.progress || 0,
          dependencies: task.depends_on_task_id || '',
          custom_class: `gantt-task-${effectiveStatus}${catIdx >= 0 ? ` gantt-cat-${catIdx}` : ''}`,
          // Store additional data
          _task: { ...task, status: effectiveStatus },
          _project: project?.name || 'No Project',
          _originalTitle: task.title, // Keep original title for popup
          _catIdx: catIdx,
        };
      });

    if (ganttTasks.length === 0) {
      ganttRef.current.innerHTML = '<div class="text-center py-20 text-stone-500">No tasks with dates to display. Add start/end dates to your tasks.</div>';
      return;
    }

    // NO range clamping: let Frappe Gantt render ALL tasks at their real dates.
    // Admin can scroll horizontally to explore the full timeline.
    // After rendering, we auto-scroll to center the tasks on screen.

    if (ganttTasks.length === 0) {
      ganttRef.current.innerHTML = '<div class="text-center py-20 text-stone-500">No tasks with dates to display. Add start/end dates to your tasks.</div>';
      return;
    }

    try {
      ganttInstance.current = new Gantt(ganttRef.current, ganttTasks, {
        view_mode: viewMode,
        date_format: 'YYYY-MM-DD',
        popup_trigger: 'click',
        bar_height: 24,
        padding: 20,
        custom_popup_html: (task) => {
          const originalTask = task._task;
          const statusLabel = (originalTask?.status || 'not_started').replace(/_/g, ' ');
          const progressPercent = originalTask?.progress || 0;
          const isCompleted = originalTask?.status === 'completed' || originalTask?.status === 'fully_completed';
          const displayTitle = task._originalTitle || task.name.split(' (')[0]; // Use original title
          
          return `
            <div class="gantt-popup p-4 bg-white rounded-lg shadow-xl border border-stone-200 min-w-[320px]">
              <h3 class="font-semibold text-lg text-stone-900 mb-1">${displayTitle}</h3>
              <p class="text-sm text-stone-500 mb-4">${task._project}</p>
              
              <div class="flex justify-between items-center mb-3">
                <span class="text-sm text-stone-600 font-medium">Schedule</span>
                ${isAdmin && !isCompleted ? `
                <button onclick="window.ganttEditDates('${task.id}')" class="flex items-center gap-1 px-2 py-1 text-sm text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
                  Edit Dates
                </button>
                ` : ''}
              </div>
              
              <div class="grid grid-cols-2 gap-3 mb-4">
                <div class="p-3 bg-stone-50 rounded-lg">
                  <p class="text-xs text-stone-500 mb-1">Start Date</p>
                  <p class="text-sm font-medium text-stone-900">${task.start || 'Not set'}</p>
                </div>
                <div class="p-3 bg-red-50 rounded-lg">
                  <p class="text-xs text-red-600 mb-1">End Date (Deadline)</p>
                  <p class="text-sm font-medium text-stone-900">${task.end || 'Not set'}</p>
                </div>
              </div>
              
              <div class="space-y-2 text-sm mb-4">
                <div class="flex justify-between items-center">
                  <span class="text-stone-500">Current Status:</span>
                  <select onchange="window.ganttChangeStatus('${task.id}', this.value)" 
                    class="px-2 py-1 rounded-md text-xs font-medium border border-stone-200 bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400"
                    style="min-width: 140px;"
                    data-testid="popup-status-${task.id}">
                    <option value="not_started" ${originalTask?.status === 'not_started' || !originalTask?.status ? 'selected' : ''}>Not Started</option>
                    <option value="in_progress" ${originalTask?.status === 'in_progress' ? 'selected' : ''}>In Progress</option>
                    <option value="completed" ${originalTask?.status === 'completed' ? 'selected' : ''}>Completed</option>
                    <option value="completed_with_snags" ${originalTask?.status === 'completed_with_snags' ? 'selected' : ''}>Completed with Snags</option>
                    <option value="delayed" ${originalTask?.status === 'delayed' ? 'selected' : ''}>Delayed</option>
                    <option value="awaiting_client_approval" ${originalTask?.status === 'awaiting_client_approval' ? 'selected' : ''}>Awaiting Approval</option>
                  </select>
                </div>
                <div class="flex justify-between">
                  <span class="text-stone-500">Progress:</span>
                  <span class="font-medium">${progressPercent}%</span>
                </div>
                ${originalTask?.depends_on_task_id ? `
                <div class="flex justify-between">
                  <span class="text-stone-500">Depends on:</span>
                  <span class="font-medium text-purple-600">${getDependencyTaskName(originalTask.depends_on_task_id)}</span>
                </div>
                ` : ''}
              </div>
              
              <div class="flex gap-2 pt-3 border-t border-stone-100">
                <button onclick="document.querySelector('.popup-wrapper')?.remove()" class="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-stone-100 text-stone-700 text-sm rounded-md hover:bg-stone-200">
                  Close
                </button>
                <button onclick="window.ganttOpenFullTask('${task.id}')" class="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>
                  Open Full Task
                </button>
              </div>
              ${isAdmin && !isCompleted ? `
              <div class="mt-2">
                <button onclick="window.ganttAddDependency('${task.id}')" class="w-full flex items-center justify-center gap-1 px-3 py-2 bg-purple-100 text-purple-700 text-sm rounded-md hover:bg-purple-200">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                  Add Dependency
                </button>
              </div>
              ` : ''}
            </div>
          `;
        },
        on_click: (task) => {
          // Use refs to access current state in callback
          const isDepMode = dependencyModeRef.current;
          console.log('Gantt on_click:', { taskId: task.id, dependencyMode: isDepMode, isAdmin });
          
          if (isDepMode && isAdmin) {
            handleDependencyClick(task.id);
          }
        },
        on_date_change: async (task, start, end) => {
          // ROLE CHECK: Allow both admins and vendors to drag/reschedule
          const canDrag = isAdmin || user?.role === 'vendor';
          
          if (!canDrag) {
            toast.error('View-only mode', { 
              icon: <Eye size={16} />,
              description: 'Clients cannot reschedule tasks'
            });
            fetchData(); // Refresh to reset
            return;
          }
          
          // Find the original task to check status
          const originalTask = tasks.find(t => t.id === task.id);
          
          // PHASE 3: Block completed tasks
          if (originalTask?.status === 'completed' || originalTask?.status === 'fully_completed') {
            toast.error('Cannot reschedule completed tasks', { 
              icon: <Lock size={16} />,
              description: 'Completed tasks are locked from rescheduling'
            });
            fetchData();
            return;
          }
          
          // P1 ENHANCEMENT: REAL-TIME date label update during drag
          // Format dates for display (DD/MM format)
          const formatDateLabel = (date) => {
            const d = String(date.getDate()).padStart(2, '0');
            const m = String(date.getMonth() + 1).padStart(2, '0');
            return `${d}/${m}`;
          };
          
          const startDateStr = formatDateLabel(start);
          const endDateStr = formatDateLabel(end);
          
          // Update bar label IMMEDIATELY for real-time visual feedback
          const barElement = document.querySelector(`[data-id="${task.id}"] .bar-label`);
          if (barElement) {
            const originalTitle = originalTask?.title || task.name.split(' (')[0];
            barElement.textContent = `${originalTitle} (${startDateStr} - ${endDateStr})`;
          }
          
          // DEBOUNCED API CALL: Only call API after user stops dragging (300ms idle)
          // Clear any pending debounce timer
          if (window.ganttDragDebounceTimer) {
            clearTimeout(window.ganttDragDebounceTimer);
          }
          
          // Set new debounce timer for API call
          window.ganttDragDebounceTimer = setTimeout(async () => {
            try {
              // PHASE 1 & 2: Validate the drag operation
              const validation = await axios.post(`${API}/tasks/validate-drag`, {
                task_id: task.id,
                new_start_date: start.toISOString(),
                new_end_date: end.toISOString(),
              });
              
              const result = validation.data;
              
              // If validation has errors, block and show toast
              if (!result.valid) {
                result.errors.forEach(err => {
                  toast.error(err, { duration: 5000, icon: <AlertCircle size={16} /> });
                });
                fetchData(); // Reset to original position
                return;
              }
              
              // If there are warnings but no cascade needed, show warnings and proceed
              if (result.warnings.length > 0 && !result.requires_cascade) {
                result.warnings.forEach(warn => {
                  toast.warning(warn, { duration: 4000 });
                });
              }
              
              // PHASE 2: If cascade is required, show confirmation dialog
              if (result.requires_cascade && result.affected_tasks.length > 0) {
                setPendingDrag({
                  taskId: task.id,
                  taskTitle: originalTask?.title || task.name,
                  newStart: start.toISOString(),
                  newEnd: end.toISOString(),
                });
                setDragValidation(result);
                setShowDragValidation(true);
                // Don't apply yet - wait for user confirmation
                return;
              }
              
              // No cascade needed - apply directly
              await axios.put(`${API}/tasks/${task.id}`, {
                start_date: start.toISOString(),
                deadline: end.toISOString(),
              });
              
              toast.success('Task dates updated');
              
              // Show warnings if any
              if (result.warnings.length > 0) {
                result.warnings.forEach(warn => {
                  toast.warning(warn, { duration: 4000 });
                });
              }
              
            } catch (error) {
              console.error('Drag validation error:', error);
              const errorMsg = error.response?.data?.detail || 'Failed to update task dates';
              toast.error(errorMsg);
              fetchData(); // Refresh to reset
            }
          }, 300); // 300ms debounce delay
        },
        on_progress_change: async (task, progress) => {
          if (!isAdmin && user?.role !== 'vendor') {
            toast.error('Only admins and vendors can update progress');
            fetchData();
            return;
          }
          
          try {
            await axios.put(`${API}/tasks/${task.id}`, {
              progress: Math.round(progress),
            });
            toast.success('Progress updated');
          } catch (error) {
            toast.error('Failed to update progress');
            fetchData();
          }
        },
      });

      // CENTER tasks on screen: scroll .gantt-container (Frappe Gantt's wrapper inside ganttRef)
      // so the midpoint of the task bars is at the center of the visible viewport.
      setTimeout(() => {
        // Frappe Gantt creates its own .gantt-container inside our ref element
        const scrollContainer = ganttRef.current?.querySelector('.gantt-container') || ganttRef.current;
        if (!scrollContainer || scrollContainer.scrollWidth <= scrollContainer.clientWidth) return;

        // Find all bar elements to compute the center of the task cluster
        const bars = scrollContainer.querySelectorAll('.bar-wrapper .bar');
        if (!bars || bars.length === 0) return;
        let minX = Infinity, maxRight = 0;
        bars.forEach(bar => {
          const x = parseFloat(bar.getAttribute('x')) || 0;
          const w = parseFloat(bar.getAttribute('width')) || 0;
          if (x < minX) minX = x;
          if (x + w > maxRight) maxRight = x + w;
        });
        const taskCenterX = (minX + maxRight) / 2;
        const viewportCenter = scrollContainer.clientWidth / 2;
        scrollContainer.scrollLeft = Math.max(0, taskCenterX - viewportCenter);
      }, 200);

      // Add custom CSS for status colors
      const style = document.createElement('style');
      style.textContent = `
        .gantt-task-not_started .bar { fill: ${statusColors.not_started.fill} !important; }
        .gantt-task-in_progress .bar { fill: ${statusColors.in_progress.fill} !important; }
        .gantt-task-completed .bar { fill: ${statusColors.completed.fill} !important; }
        .gantt-task-completed_with_snags .bar { fill: ${statusColors.completed_with_snags.fill} !important; }
        .gantt-task-delayed .bar { fill: ${statusColors.delayed.fill} !important; }
        .gantt-task-awaiting_client_approval .bar { fill: ${statusColors.awaiting_client_approval.fill} !important; }
        
        .gantt .bar-wrapper:hover .bar {
          filter: brightness(1.1);
        }
        
        .gantt .bar-progress {
          fill: rgba(0,0,0,0.2) !important;
        }
        
        .gantt .arrow {
          stroke: #6366f1 !important;
          stroke-width: 2px !important;
        }
        
        .gantt-popup {
          z-index: 1000;
        }
        
        ${dependencyMode ? `
        .gantt .bar-wrapper {
          cursor: crosshair !important;
        }
        .gantt .bar-wrapper:hover .bar {
          stroke: #7c3aed !important;
          stroke-width: 3px !important;
        }
        ` : ''}
      `;
      document.head.appendChild(style);

      // Inject category color indicators on Gantt bars
      setTimeout(() => {
        if (!ganttRef.current) return;
        const barWrappers = ganttRef.current.querySelectorAll('.bar-wrapper');
        barWrappers.forEach(wrapper => {
          const cls = wrapper.getAttribute('class') || '';
          const catMatch = cls.match(/gantt-cat-(\d+)/);
          if (!catMatch) return;
          const catIdx = parseInt(catMatch[1]);
          const color = CATEGORY_COLORS[catIdx % CATEGORY_COLORS.length];
          
          const barRect = wrapper.querySelector('.bar');
          if (!barRect) return;
          const x = parseFloat(barRect.getAttribute('x')) || 0;
          const y = parseFloat(barRect.getAttribute('y')) || 0;
          const h = parseFloat(barRect.getAttribute('height')) || 24;
          
          // Create colored left-edge indicator
          const indicator = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
          indicator.setAttribute('x', x);
          indicator.setAttribute('y', y);
          indicator.setAttribute('width', '5');
          indicator.setAttribute('height', h);
          indicator.setAttribute('rx', '2');
          indicator.setAttribute('fill', color);
          indicator.classList.add('cat-indicator');
          // Append to the bar's parent group (bar-group), not the wrapper
          barRect.parentNode.appendChild(indicator);
        });
      }, 300);

      // Fix Frappe Gantt popup: reposition after each click so it stays within viewport.
      // Frappe Gantt creates popup once and reuses it, so we use a periodic checker.
      const repositionPopup = () => {
        const ganttCtr = ganttRef.current?.querySelector('.gantt-container') || ganttRef.current;
        if (!ganttCtr) return;
        const popup = ganttCtr.querySelector('.popup-wrapper');
        if (!popup || popup.offsetHeight === 0) return;
        
        const containerRect = ganttCtr.getBoundingClientRect();
        const origTop = parseFloat(popup.style.top) || 0;
        const origLeft = parseFloat(popup.style.left) || 0;
        
        // Only reposition if still using absolute positioning
        if (popup.dataset.repositioned === 'true' && popup.style.position === 'fixed') return;
        
        const fixedTop = containerRect.top + origTop - ganttCtr.scrollTop;
        const fixedLeft = containerRect.left + origLeft - ganttCtr.scrollLeft;
        const popupH = popup.offsetHeight || 350;
        const popupW = popup.offsetWidth || 340;
        const vH = window.innerHeight;
        const vW = window.innerWidth;
        
        const safeTop = fixedTop + popupH > vH ? Math.max(10, vH - popupH - 20) : Math.max(10, fixedTop);
        const safeLeft = fixedLeft + popupW > vW ? Math.max(10, vW - popupW - 20) : Math.max(10, fixedLeft);
        
        popup.style.position = 'fixed';
        popup.style.top = safeTop + 'px';
        popup.style.left = safeLeft + 'px';
        popup.style.zIndex = '9999';
        popup.dataset.repositioned = 'true';
      };
      
      // Watch for popup becoming visible (Frappe Gantt reuses the same element)
      const ganttCtr = ganttRef.current?.querySelector('.gantt-container') || ganttRef.current;
      if (ganttCtr) {
        const popupObserver = new MutationObserver(() => {
          // Reset repositioned flag when popup content changes
          const popup = ganttCtr.querySelector('.popup-wrapper');
          if (popup) popup.dataset.repositioned = '';
          setTimeout(repositionPopup, 80);
        });
        popupObserver.observe(ganttCtr, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
      }

      // Setup global handlers for popup buttons
      window.ganttViewTask = (taskId) => {
        const task = tasks.find(t => t.id === taskId);
        if (task) {
          navigate(`/tasks?project=${task.project_id}&task=${taskId}`);
        }
      };

      // Open Full Task in a new tab
      window.ganttOpenFullTask = (taskId) => {
        const task = tasks.find(t => t.id === taskId);
        if (task) {
          window.open(`/tasks?project=${task.project_id}&task=${taskId}`, '_blank');
        }
      };

      // Change task status from popup dropdown - real-time update
      window.ganttChangeStatus = async (taskId, newStatus) => {
        try {
          await axios.put(`${API}/tasks/${taskId}`, { status: newStatus });
          // Update local state
          setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
          // Update the Gantt bar color
          if (ganttRef.current) {
            const bar = ganttRef.current.querySelector(`[data-id="${taskId}"]`);
            if (bar) {
              bar.className = bar.className.replace(/gantt-task-\w+/g, `gantt-task-${newStatus}`);
              const rect = bar.querySelector('.bar');
              if (rect) rect.style.fill = (statusColors[newStatus] || statusColors.not_started).fill;
            }
          }
          toast.success(`Status updated to ${newStatus.replace(/_/g, ' ')}`);
        } catch (err) {
          toast.error('Failed to update status');
        }
      };

      // Edit Dates handler - opens the date editor dialog
      window.ganttEditDates = (taskId) => {
        const task = tasks.find(t => t.id === taskId);
        if (task) {
          setEditingTaskDates(task);
          setDateEditStart(task.start_date ? task.start_date.split('T')[0] : '');
          setDateEditEnd(task.deadline ? task.deadline.split('T')[0] : '');
          setShowDateEditor(true);
        }
      };

      window.ganttAddDependency = (taskId) => {
        // Set the clicked task as the SUCCESSOR (the task that waits)
        // Update both state and refs immediately
        dependencyModeRef.current = true;
        dependencySourceRef.current = taskId;
        setDependencyMode(true);
        setDependencySource(taskId);
        
        console.log('ganttAddDependency activated:', { taskId, mode: dependencyModeRef.current, source: dependencySourceRef.current });
        toast.info('Click on another task to set it as the predecessor (the task that must complete first)', { duration: 5000 });
      };

      // Add a custom click handler for dependency mode that intercepts clicks on task bars
      // This is needed because frappe-gantt's popup intercepts clicks
      const handleGanttContainerClick = (e) => {
        if (!dependencyModeRef.current || !isAdmin) return;
        
        // Find the closest bar-wrapper parent
        const barWrapper = e.target.closest('.bar-wrapper');
        if (!barWrapper) return;
        
        // Extract task ID from the bar-wrapper's data-id attribute or find it in ganttTasks
        const barGroup = barWrapper.querySelector('.bar');
        if (!barGroup) return;
        
        // Get the task name from the bar-label to find the task
        const barLabel = barWrapper.querySelector('.bar-label');
        const taskName = barLabel?.textContent || '';
        const task = ganttTasks.find(t => t.name === taskName);
        
        if (task) {
          console.log('Gantt container click in dependency mode:', { taskId: task.id, taskName });
          // Prevent the popup from showing
          e.stopPropagation();
          e.preventDefault();
          handleDependencyClick(task.id);
        }
      };

      // Use capture phase to intercept before the popup handler
      ganttRef.current.addEventListener('click', handleGanttContainerClick, true);

    } catch (error) {
      console.error('Failed to initialize Gantt:', error);
      ganttRef.current.innerHTML = '<div class="text-center py-20 text-red-500">Failed to load Gantt chart. Please refresh.</div>';
    }
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const getDependencyTaskName = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    return task?.title || 'Unknown Task';
  };

  const handleDependencyClick = (taskId) => {
    const currentSource = dependencySourceRef.current;
    const isInDependencyMode = dependencyModeRef.current;
    
    console.log('handleDependencyClick called:', { taskId, currentSource, isInDependencyMode });
    
    if (!isInDependencyMode) {
      // Not in dependency mode, ignore
      return;
    }
    
    if (!currentSource) {
      // First click - this becomes the successor (task that waits)
      setDependencySource(taskId);
      dependencySourceRef.current = taskId;
      toast.info('Now click on the predecessor task (the task that must finish first)');
    } else if (taskId === currentSource) {
      toast.error('Cannot make a task depend on itself');
    } else {
      // Second click - taskId is the predecessor, currentSource is the successor
      // Show confirmation dialog
      console.log('Creating dependency:', { from: currentSource, to: taskId });
      setPendingDependency({ from: currentSource, to: taskId });
      setShowDependencyDialog(true);
    }
  };

  const confirmDependency = async () => {
    const { from, to } = pendingDependency;
    
    try {
      // The "from" task depends on "to" task (to must finish before from starts for FS)
      await axios.put(`${API}/tasks/${from}`, {
        depends_on_task_id: to,
        dependency_type: selectedDependencyType, // P1: Include dependency type
      });
      
      toast.success(`${selectedDependencyType} dependency added successfully`);
      setDependencyMode(false);
      setDependencySource(null);
      dependencyModeRef.current = false;
      dependencySourceRef.current = null;
      setShowDependencyDialog(false);
      setPendingDependency({ from: null, to: null });
      setSelectedDependencyType('FS'); // Reset to default
      fetchData(); // Refresh to show new dependency
    } catch (error) {
      toast.error('Failed to add dependency');
    }
  };

  const removeDependency = async (taskId) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, {
        depends_on_task_id: null,
      });
      toast.success('Dependency removed');
      setEditingDependency(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to remove dependency');
    }
  };

  // Edit dependency - change predecessor for an existing dependent task
  const updateDependency = async (taskId, newPredecessorId) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, {
        depends_on_task_id: newPredecessorId,
      });
      toast.success('Dependency updated successfully');
      setEditingDependency(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update dependency');
    }
  };

  // Start editing a dependency
  const startEditDependency = (task) => {
    setEditingDependency({
      taskId: task.id,
      taskTitle: task.title,
      currentPredecessorId: task.depends_on_task_id,
      newPredecessorId: task.depends_on_task_id,
    });
  };

  // Cancel editing
  const cancelEditDependency = () => {
    setEditingDependency(null);
  };

  // ============================================================================
  // DRAG VALIDATION & CASCADE HANDLERS
  // ============================================================================
  
  // Apply cascade update (shift all dependent tasks)
  const applyCascadeUpdate = async () => {
    if (!pendingDrag || !dragValidation) return;
    
    setApplyingCascade(true);
    try {
      const response = await axios.post(`${API}/tasks/cascade-update`, {
        task_id: pendingDrag.taskId,
        new_start_date: pendingDrag.newStart,
        new_end_date: pendingDrag.newEnd,
        cascade: true,
      });
      
      toast.success(response.data.message, {
        icon: <RefreshCw size={16} />,
        description: `${response.data.updated_tasks.length} task(s) rescheduled`
      });
      
      setShowDragValidation(false);
      setShowGhostPreview(false);
      removeGhostBars();
      setPendingDrag(null);
      setDragValidation(null);
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to apply cascade update';
      toast.error(errorMsg);
    } finally {
      setApplyingCascade(false);
    }
  };
  
  // Apply only the main task update (no cascade)
  const applyWithoutCascade = async () => {
    if (!pendingDrag) return;
    
    setApplyingCascade(true);
    try {
      await axios.post(`${API}/tasks/cascade-update`, {
        task_id: pendingDrag.taskId,
        new_start_date: pendingDrag.newStart,
        new_end_date: pendingDrag.newEnd,
        cascade: false,
      });
      
      toast.success('Task dates updated (dependent tasks not shifted)', {
        description: 'Dependent tasks may now have scheduling conflicts'
      });
      
      setShowDragValidation(false);
      setShowGhostPreview(false);
      removeGhostBars();
      setPendingDrag(null);
      setDragValidation(null);
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update task';
      toast.error(errorMsg);
    } finally {
      setApplyingCascade(false);
    }
  };
  
  // Cancel drag operation
  const cancelDragOperation = () => {
    setShowDragValidation(false);
    setShowGhostPreview(false);
    setPendingDrag(null);
    setDragValidation(null);
    fetchData(); // Reset to original positions
  };
  
  // ============================================================================
  // P0 FEATURE: GHOST PREVIEW VISUALIZATION
  // ============================================================================
  
  // Show ghost preview when cascade dialog opens
  useEffect(() => {
    if (showDragValidation && dragValidation?.affected_tasks?.length > 0) {
      setShowGhostPreview(true);
      // Add ghost bars to the Gantt chart
      renderGhostBars();
    } else {
      setShowGhostPreview(false);
      // Remove ghost bars
      removeGhostBars();
    }
  }, [showDragValidation, dragValidation]);
  
  // Render ghost preview bars on the Gantt SVG
  const renderGhostBars = () => {
    if (!ganttInstance.current || !dragValidation?.affected_tasks) return;
    
    try {
      const svg = ganttRef.current?.querySelector('svg.gantt');
      if (!svg) return;
      
      // Remove existing ghost bars
      removeGhostBars();
      
      // Create ghost preview container
      const ghostGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      ghostGroup.setAttribute('class', 'gantt-ghost-group');
      ghostGroup.setAttribute('data-ghost', 'true');
      
      // Get Gantt chart's timeline info
      const gantt = ganttInstance.current;
      const barWrappers = svg.querySelectorAll('.bar-wrapper');
      
      // Map task IDs to their bar positions
      const taskPositions = {};
      barWrappers.forEach(wrapper => {
        const bar = wrapper.querySelector('.bar');
        if (bar) {
          const taskId = wrapper.getAttribute('data-id');
          const transform = bar.getAttribute('transform') || '';
          const x = parseFloat(bar.getAttribute('x')) || 0;
          const y = parseFloat(bar.getAttribute('y')) || 0;
          const width = parseFloat(bar.getAttribute('width')) || 100;
          const height = parseFloat(bar.getAttribute('height')) || 20;
          taskPositions[taskId] = { x, y, width, height, transform };
        }
      });
      
      // Calculate pixels per day based on view mode
      const pixelsPerDay = viewMode === 'Day' ? 38 : viewMode === 'Week' ? 18 : viewMode === 'Month' ? 9 : viewMode === 'Year' ? 2 : 18;
      
      // Create ghost bars for each affected task
      dragValidation.affected_tasks.forEach((affectedTask, index) => {
        const existingPos = taskPositions[affectedTask.id];
        if (!existingPos) return;
        
        // Calculate new position based on shift days
        const shiftPixels = affectedTask.shift_days * pixelsPerDay;
        const newX = existingPos.x + shiftPixels;
        
        // Create ghost bar group
        const ghostBar = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        ghostBar.setAttribute('class', 'gantt-ghost-bar-group');
        ghostBar.setAttribute('data-task-id', affectedTask.id);
        
        // Ghost rectangle (dashed outline)
        const ghostRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        ghostRect.setAttribute('x', newX);
        ghostRect.setAttribute('y', existingPos.y);
        ghostRect.setAttribute('width', existingPos.width);
        ghostRect.setAttribute('height', existingPos.height);
        ghostRect.setAttribute('rx', '3');
        ghostRect.setAttribute('ry', '3');
        ghostRect.setAttribute('class', 'gantt-ghost-rect');
        ghostRect.setAttribute('fill', 'rgba(99, 102, 241, 0.15)');
        ghostRect.setAttribute('stroke', '#6366f1');
        ghostRect.setAttribute('stroke-width', '2');
        ghostRect.setAttribute('stroke-dasharray', '4 2');
        
        // Ghost label (shift indicator)
        const ghostLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        ghostLabel.setAttribute('x', newX + existingPos.width / 2);
        ghostLabel.setAttribute('y', existingPos.y - 5);
        ghostLabel.setAttribute('text-anchor', 'middle');
        ghostLabel.setAttribute('class', 'gantt-ghost-label');
        ghostLabel.setAttribute('fill', '#6366f1');
        ghostLabel.setAttribute('font-size', '10');
        ghostLabel.setAttribute('font-weight', '600');
        ghostLabel.textContent = `+${affectedTask.shift_days}d`;
        
        // Arrow connecting old to new position
        const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        arrow.setAttribute('x1', existingPos.x + existingPos.width);
        arrow.setAttribute('y1', existingPos.y + existingPos.height / 2);
        arrow.setAttribute('x2', newX);
        arrow.setAttribute('y2', existingPos.y + existingPos.height / 2);
        arrow.setAttribute('stroke', '#6366f1');
        arrow.setAttribute('stroke-width', '1.5');
        arrow.setAttribute('stroke-dasharray', '3 2');
        arrow.setAttribute('class', 'gantt-ghost-arrow');
        
        ghostBar.appendChild(arrow);
        ghostBar.appendChild(ghostRect);
        ghostBar.appendChild(ghostLabel);
        ghostGroup.appendChild(ghostBar);
        
        // Add highlight class to original task bar
        const originalWrapper = svg.querySelector(`.bar-wrapper[data-id="${affectedTask.id}"]`);
        if (originalWrapper) {
          originalWrapper.classList.add('cascade-affected');
        }
      });
      
      svg.appendChild(ghostGroup);
    } catch (error) {
      console.error('Error rendering ghost bars:', error);
    }
  };
  
  // Remove ghost preview bars
  const removeGhostBars = () => {
    const svg = ganttRef.current?.querySelector('svg.gantt');
    if (!svg) return;
    
    // Remove ghost group
    const ghostGroup = svg.querySelector('.gantt-ghost-group');
    if (ghostGroup) {
      ghostGroup.remove();
    }
    
    // Remove highlight classes from affected tasks
    svg.querySelectorAll('.bar-wrapper.cascade-affected').forEach(wrapper => {
      wrapper.classList.remove('cascade-affected');
    });
  };

  // Save edited dates
  const saveDateEdits = async () => {
    if (!editingTaskDates) return;
    
    setSavingDates(true);
    try {
      // Validate dates
      if (dateEditStart && dateEditEnd) {
        const startDate = new Date(dateEditStart);
        const endDate = new Date(dateEditEnd);
        if (endDate < startDate) {
          toast.error('End date cannot be before start date');
          setSavingDates(false);
          return;
        }
      }
      
      // Use the validation endpoint to check for dependency conflicts
      const validation = await axios.post(`${API}/tasks/validate-drag`, {
        task_id: editingTaskDates.id,
        new_start_date: dateEditStart ? new Date(dateEditStart).toISOString() : editingTaskDates.start_date,
        new_end_date: dateEditEnd ? new Date(dateEditEnd).toISOString() : editingTaskDates.deadline,
      });
      
      const result = validation.data;
      
      // If there are errors, show them
      if (!result.valid) {
        result.errors.forEach(err => {
          toast.error(err, { duration: 5000 });
        });
        setSavingDates(false);
        return;
      }
      
      // If cascade is required, show the cascade dialog
      if (result.requires_cascade && result.affected_tasks.length > 0) {
        setPendingDrag({
          taskId: editingTaskDates.id,
          taskTitle: editingTaskDates.title,
          newStart: dateEditStart ? new Date(dateEditStart).toISOString() : editingTaskDates.start_date,
          newEnd: dateEditEnd ? new Date(dateEditEnd).toISOString() : editingTaskDates.deadline,
        });
        setDragValidation(result);
        setShowDateEditor(false);
        setShowDragValidation(true);
        setSavingDates(false);
        return;
      }
      
      // No cascade needed - update directly
      await axios.put(`${API}/tasks/${editingTaskDates.id}`, {
        start_date: dateEditStart ? new Date(dateEditStart).toISOString() : null,
        deadline: dateEditEnd ? new Date(dateEditEnd).toISOString() : null,
      });
      
      toast.success('Task dates updated successfully');
      setShowDateEditor(false);
      setEditingTaskDates(null);
      fetchData();
      
      // Show warnings if any
      if (result.warnings.length > 0) {
        result.warnings.forEach(warn => {
          toast.warning(warn, { duration: 4000 });
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update dates');
    } finally {
      setSavingDates(false);
    }
  };

  // ============================================================================
  // TASK SORTING & REORDERING (Visual Only - No Effect on Dependencies)
  // ============================================================================
  
  // Get sorted tasks based on current sort option
  const getSortedTasks = () => {
    if (!tasks.length) return [];
    
    let sorted = [...tasks];
    
    switch (sortBy) {
      case 'dependency':
        // Sort by dependency chain - predecessors first
        const taskMap = new Map(tasks.map(t => [t.id, t]));
        const visited = new Set();
        const result = [];
        
        const visit = (taskId) => {
          if (visited.has(taskId)) return;
          const task = taskMap.get(taskId);
          if (!task) return;
          
          // Visit predecessor first
          if (task.depends_on_task_id && taskMap.has(task.depends_on_task_id)) {
            visit(task.depends_on_task_id);
          }
          
          visited.add(taskId);
          result.push(task);
        };
        
        tasks.forEach(t => visit(t.id));
        sorted = result;
        break;
        
      case 'category':
        sorted.sort((a, b) => (a.category_name || a.category_code || '').localeCompare(b.category_name || b.category_code || ''));
        break;
        
      case 'vendor':
        sorted.sort((a, b) => (a.assigned_to || '').localeCompare(b.assigned_to || ''));
        break;
        
      case 'start_date':
        sorted.sort((a, b) => {
          // First sort by display_order if available
          const orderA = a.display_order ?? 999;
          const orderB = b.display_order ?? 999;
          if (orderA !== orderB) return orderA - orderB;
          // Then by start date
          return new Date(a.start_date || a.deadline) - new Date(b.start_date || b.deadline);
        });
        break;
        
      case 'custom':
        // Use custom task order
        if (taskOrder.length > 0) {
          const orderMap = new Map(taskOrder.map((id, idx) => [id, idx]));
          sorted.sort((a, b) => (orderMap.get(a.id) ?? 999) - (orderMap.get(b.id) ?? 999));
        }
        break;
        
      default:
        // Default: by display_order if available, then by start date
        sorted.sort((a, b) => {
          // First sort by display_order if available
          const orderA = a.display_order ?? 999;
          const orderB = b.display_order ?? 999;
          if (orderA !== orderB) return orderA - orderB;
          // Then by start date
          return new Date(a.start_date || a.deadline) - new Date(b.start_date || b.deadline);
        });
    }
    
    return sorted;
  };
  
  // Handle drag start in reorder mode
  const handleReorderDragStart = (e, taskId) => {
    setDraggedTaskId(taskId);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  // Handle drag over in reorder mode
  const handleReorderDragOver = (e, targetTaskId) => {
    e.preventDefault();
    if (draggedTaskId === targetTaskId) return;
  };
  
  // Handle drop in reorder mode
  const handleReorderDrop = (e, targetTaskId) => {
    e.preventDefault();
    if (!draggedTaskId || draggedTaskId === targetTaskId) return;
    
    const currentOrder = taskOrder.length > 0 ? [...taskOrder] : tasks.map(t => t.id);
    const draggedIdx = currentOrder.indexOf(draggedTaskId);
    const targetIdx = currentOrder.indexOf(targetTaskId);
    
    if (draggedIdx === -1 || targetIdx === -1) return;
    
    // Remove dragged item and insert at target position
    currentOrder.splice(draggedIdx, 1);
    currentOrder.splice(targetIdx, 0, draggedTaskId);
    
    setTaskOrder(currentOrder);
    setSortBy('custom');
    setDraggedTaskId(null);
    
    toast.success('Task order updated', {
      description: 'This is a visual change only - dependencies are preserved'
    });
  };
  
  // Save task order to backend (persists across all users/views)
  const saveTaskOrder = async () => {
    if (taskOrder.length === 0) {
      toast.error('No custom order to save');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/tasks/update-order`, {
        task_ids: taskOrder,
        project_id: selectedProject !== 'all' ? selectedProject : null,
      });
      
      toast.success(response.data.message, {
        description: 'Order saved to database - visible to all users'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save task order');
    }
  };

  // Quick dependency add from dropdown
  const addDependencyFromDropdown = async (successorTaskId, predecessorTaskId) => {
    if (!predecessorTaskId) {
      // Remove dependency if "None" selected
      await removeDependency(successorTaskId);
      return;
    }
    
    try {
      await axios.put(`${API}/tasks/${successorTaskId}`, {
        depends_on_task_id: predecessorTaskId,
      });
      toast.success('Dependency updated');
      setSelectedTaskForDep(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update dependency');
    }
  };

  // Get tasks that can be predecessors (exclude the task itself and tasks that depend on it)
  const getValidPredecessors = (taskId) => {
    return tasks.filter(t => {
      if (t.id === taskId) return false; // Can't depend on itself
      if (t.depends_on_task_id === taskId) return false; // Would create circular dependency
      return true;
    });
  };

  // Get tasks with existing dependencies for the manager view
  const tasksWithDependencies = tasks.filter(t => t.depends_on_task_id);

  const cancelDependencyMode = () => {
    setDependencyMode(false);
    setDependencySource(null);
    dependencyModeRef.current = false;
    dependencySourceRef.current = null;
    setShowDependencyDialog(false);
    setPendingDependency({ from: null, to: null });
  };

  const changeViewMode = (mode) => {
    setViewMode(mode);
    if (ganttInstance.current) {
      ganttInstance.current.change_view_mode(mode);
    }
  };

  // P2: Fetch AI Dependency Suggestions
  const fetchAISuggestions = async () => {
    setLoadingAI(true);
    try {
      const params = selectedProject !== 'all' ? `?project_id=${selectedProject}` : '';
      const response = await axios.post(`${API}/tasks/ai-suggest-dependencies${params}`);
      setAiSuggestions(response.data.suggestions || []);
      setShowAISuggestions(true);
      
      if (response.data.suggestions.length === 0) {
        toast.info(response.data.message || 'No dependency suggestions found');
      } else {
        toast.success(`Found ${response.data.suggestions.length} dependency suggestions`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get AI suggestions');
    } finally {
      setLoadingAI(false);
    }
  };

  // ENHANCED AI: Arrange tasks, set dates, and group by category
  const [aiArrangements, setAiArrangements] = useState(null);
  const [showArrangePanel, setShowArrangePanel] = useState(false);
  const [loadingArrange, setLoadingArrange] = useState(false);

  const fetchAIArrangements = async () => {
    if (selectedProject === 'all') {
      toast.error('Please select a specific project for AI task arrangement');
      return;
    }
    
    setLoadingArrange(true);
    try {
      const response = await axios.post(`${API}/tasks/ai-arrange-tasks?project_id=${selectedProject}`);
      setAiArrangements(response.data);
      setShowArrangePanel(true);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get AI arrangements');
    } finally {
      setLoadingArrange(false);
    }
  };

  const applyAIArrangements = async () => {
    if (!aiArrangements) return;
    
    try {
      const response = await axios.post(`${API}/tasks/apply-arrangements`, {
        arrangements: aiArrangements.arrangements,
        dependencies: aiArrangements.dependencies
      });
      
      toast.success(response.data.message);
      setShowArrangePanel(false);
      setAiArrangements(null);
      fetchData(); // Refresh Gantt
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply arrangements');
    }
  };

  // AI Pro: Project Timeline Prediction - Analyzes if project is running ahead/behind schedule
  const fetchAiProPrediction = async () => {
    if (selectedProject === 'all') {
      toast.error('Please select a specific project for AI Pro analysis');
      return;
    }
    
    setLoadingAiPro(true);
    try {
      // Get project details
      const project = projects.find(p => p.id === selectedProject);
      if (!project) {
        toast.error('Project not found');
        return;
      }
      
      // Filter tasks for selected project
      const projectTasks = tasks.filter(t => t.project_id === selectedProject);
      if (projectTasks.length === 0) {
        toast.error('No tasks found for this project');
        return;
      }
      
      // Calculate prediction metrics
      const today = new Date();
      const projectStart = project.start_date ? new Date(project.start_date) : null;
      const projectEnd = project.end_date ? new Date(project.end_date) : null;
      
      // Task completion analysis
      const totalTasks = projectTasks.length;
      const completedTasks = projectTasks.filter(t => t.status === 'completed').length;
      const inProgressTasks = projectTasks.filter(t => t.status === 'in_progress').length;
      const delayedTasks = projectTasks.filter(t => t.status === 'delayed').length;
      const completionPercentage = Math.round((completedTasks / totalTasks) * 100);
      
      // Timeline analysis
      let daysRemaining = 0;
      let daysPassed = 0;
      let expectedProgress = 0;
      let timelineStatus = 'on_track';
      let daysDifference = 0;
      
      if (projectStart && projectEnd) {
        const totalDuration = Math.ceil((projectEnd - projectStart) / (1000 * 60 * 60 * 24));
        daysPassed = Math.ceil((today - projectStart) / (1000 * 60 * 60 * 24));
        daysRemaining = Math.ceil((projectEnd - today) / (1000 * 60 * 60 * 24));
        expectedProgress = Math.min(100, Math.round((daysPassed / totalDuration) * 100));
        
        // Compare actual vs expected progress
        const progressDiff = completionPercentage - expectedProgress;
        
        if (progressDiff >= 10) {
          timelineStatus = 'ahead';
          daysDifference = Math.round((progressDiff / 100) * totalDuration);
        } else if (progressDiff <= -10) {
          timelineStatus = 'behind';
          daysDifference = Math.abs(Math.round((progressDiff / 100) * totalDuration));
        } else {
          timelineStatus = 'on_track';
          daysDifference = 0;
        }
      }
      
      // Check for overdue tasks (tasks past deadline but not completed)
      const overdueTasks = projectTasks.filter(t => {
        if (t.status === 'completed') return false;
        const deadline = new Date(t.deadline);
        return deadline < today;
      });
      
      // Predict completion date based on current velocity
      let predictedEndDate = projectEnd;
      if (completedTasks > 0 && projectStart) {
        const avgDaysPerTask = daysPassed / completedTasks;
        const remainingTasks = totalTasks - completedTasks;
        const estimatedDaysRemaining = remainingTasks * avgDaysPerTask;
        predictedEndDate = new Date(today.getTime() + estimatedDaysRemaining * 24 * 60 * 60 * 1000);
      }
      
      const prediction = {
        project_name: project.name,
        project_start: projectStart ? formatDate(projectStart) : undefined,
        project_end: projectEnd ? formatDate(projectEnd) : undefined,
        total_tasks: totalTasks,
        completed_tasks: completedTasks,
        in_progress_tasks: inProgressTasks,
        delayed_tasks: delayedTasks,
        overdue_tasks: overdueTasks.length,
        completion_percentage: completionPercentage,
        expected_progress: expectedProgress,
        timeline_status: timelineStatus,
        days_difference: daysDifference,
        days_remaining: daysRemaining,
        predicted_end_date: predictedEndDate ? formatDate(predictedEndDate) : undefined,
        risk_factors: [],
        recommendations: [],
      };
      
      // Add risk factors
      if (overdueTasks.length > 0) {
        prediction.risk_factors.push(`${overdueTasks.length} overdue task(s)`);
      }
      if (delayedTasks > 0) {
        prediction.risk_factors.push(`${delayedTasks} task(s) marked as delayed`);
      }
      if (timelineStatus === 'behind') {
        prediction.risk_factors.push(`Project running ${daysDifference} days behind schedule`);
      }
      
      // Add recommendations
      if (timelineStatus === 'behind') {
        prediction.recommendations.push('Consider adding resources to critical path tasks');
        prediction.recommendations.push('Review scope with team to identify tasks that can be fast-tracked');
      }
      if (overdueTasks.length > 0) {
        prediction.recommendations.push('Prioritize overdue tasks immediately');
      }
      if (completionPercentage < 50 && daysRemaining < 14) {
        prediction.recommendations.push('High risk of missing deadline - escalate to management');
      }
      
      setAiProPrediction(prediction);
      setShowAiProPanel(true);
      
      if (timelineStatus === 'ahead') {
        toast.success(`Project running ${daysDifference} days ahead of schedule!`);
      } else if (timelineStatus === 'behind') {
        toast.warning(`Project running ${daysDifference} days behind schedule`);
      } else {
        toast.info('Project is on track');
      }
    } catch (error) {
      toast.error('Failed to analyze project timeline');
      console.error('AI Pro error:', error);
    } finally {
      setLoadingAiPro(false);
    }
  };

  // P0 NEW: Quick Create Task from Gantt Chart
  const handleQuickCreate = async () => {
    if (!quickCreateData.title || !quickCreateData.project_id) {
      toast.error('Please fill in task title and select a project');
      return;
    }
    if (!quickCreateData.main_category_id) {
      toast.error('Please select a Main Category');
      return;
    }
    
    try {
      const taskData = {
        title: quickCreateData.title,
        project_id: quickCreateData.project_id,
        main_category_id: quickCreateData.main_category_id,
        sub_category_id: quickCreateData.sub_category_id || undefined,
        start_date: quickCreateData.start_date ? new Date(quickCreateData.start_date).toISOString() : new Date().toISOString(),
        deadline: quickCreateData.deadline ? new Date(quickCreateData.deadline).toISOString() : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        description: quickCreateData.description || '',
        status: 'not_started',
      };
      
      await axios.post(`${API}/tasks`, taskData);
      toast.success('Task created successfully');
      setQuickCreateOpen(false);
      setQuickCreateData({
        title: '',
        project_id: selectedProject !== 'all' ? selectedProject : '',
        main_category_id: '',
        sub_category_id: '',
        start_date: '',
        deadline: '',
        description: '',
      });
      setSubCategories([]);
      setMainCatSearch('');
      setSubCatSearch('');
      fetchData(); // Refresh Gantt
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  // Fetch main categories for Quick Create dialog
  const fetchMainCategories = async () => {
    try {
      const res = await axios.get(`${API}/boq-categories/main`);
      setMainCategories(res.data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  // Fetch sub categories when main category changes
  const fetchSubCategories = async (mainCatId) => {
    if (!mainCatId) { setSubCategories([]); return; }
    try {
      const res = await axios.get(`${API}/boq-categories/sub?main_category_id=${mainCatId}`);
      setSubCategories(res.data);
    } catch (err) {
      console.error('Failed to fetch sub-categories:', err);
      setSubCategories([]);
    }
  };

  // P2: Apply AI Suggested Dependency
  const applyAISuggestion = async (suggestion) => {
    try {
      await axios.post(`${API}/tasks/apply-dependency`, {
        predecessor_task_id: suggestion.predecessor_task_id,
        successor_task_id: suggestion.successor_task_id
      });
      
      toast.success(`Dependency added: ${suggestion.predecessor_title} → ${suggestion.successor_title}`);
      
      // Remove this suggestion from the list
      setAiSuggestions(prev => prev.filter(s => 
        s.predecessor_task_id !== suggestion.predecessor_task_id || 
        s.successor_task_id !== suggestion.successor_task_id
      ));
      
      // Refresh Gantt data
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply dependency');
    }
  };

  // P2: Dismiss/reject AI suggestion (for learning)
  const dismissAISuggestion = async (suggestion, index) => {
    try {
      // Call reject endpoint for AI learning
      await axios.post(`${API}/tasks/reject-dependency-suggestion`, {
        predecessor_task_id: suggestion.predecessor_task_id,
        successor_task_id: suggestion.successor_task_id
      });
    } catch (error) {
      // Silently fail - rejection tracking is not critical
      console.warn('Failed to record rejection:', error);
    }
    
    // Remove from UI regardless
    setAiSuggestions(prev => prev.filter((_, i) => i !== index));
  };

  // Export Checklist handler with authentication
  const handleExportChecklist = async (format) => {
    try {
      const project = projects.find(p => p.id === selectedProject);
      const projectName = project?.name || 'Project';
      
      const response = await axios.get(`${API}/projects/${selectedProject}/export/checklist?format=${format}`, {
        responseType: 'blob'
      });
      
      const filename = `Checklist-${projectName.replace(/\s+/g, '_')}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Exported ${filename} successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to export checklist as ${format.toUpperCase()}`);
    }
  };

  // Get task counts by status
  const taskCounts = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    notStarted: tasks.filter(t => t.status === 'not_started').length,
    withDependencies: tasks.filter(t => t.depends_on_task_id).length,
  };

  // Category legend: unique categories with color coding
  const CATEGORY_COLORS = [
    '#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4',
    '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b',
    '#6366f1', '#84cc16', '#e11d48', '#0891b2', '#7c3aed',
  ];
  // ganttCategoryFilter state is declared at the top of the component
  const uniqueCategories = useMemo(() => {
    const catMap = {};
    tasks.forEach(t => {
      const key = t.category_name || t.category_code;
      if (key && !catMap[key]) {
        catMap[key] = { name: key, group: t.category_group || '', code: t.category_code || '', count: 0 };
      }
      if (key) catMap[key].count++;
    });
    return Object.values(catMap).sort((a, b) => (a.group || '').localeCompare(b.group || '') || a.name.localeCompare(b.name));
  }, [tasks]);

  // Filtered tasks based on BOQ + category filter
  const filteredTasks = useMemo(() => {
    let out = tasks;
    if (selectedBoqWorksheet !== 'all') {
      out = out.filter(t =>
        t.boq_worksheet_id === selectedBoqWorksheet || boqTaskIds.has(t.id)
      );
    }
    if (ganttCategoryFilter.size === 0) return out;
    return out.filter(t => ganttCategoryFilter.has(t.category_name || t.category_code));
  }, [tasks, ganttCategoryFilter, selectedBoqWorksheet, boqTaskIds]);

  return (
    <Layout>
      <div className="space-y-4 w-full overflow-x-hidden" data-testid="gantt-chart-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold text-stone-900">Gantt Chart</h1>
            <p className="text-stone-500">Visualize task timelines and dependencies</p>
          </div>
          
          <div className="flex items-center gap-3 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/calendar')}
              data-testid="switch-to-calendar"
            >
              <Calendar size={16} className="mr-2" />
              Calendar View
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/tasks')}
              data-testid="switch-to-list"
            >
              <ListTodo size={16} className="mr-2" />
              List View
            </Button>
          </div>
        </div>

        {/* Filters & Controls */}
        <div className="apple-card p-4 overflow-hidden">
          <div className="flex flex-wrap items-center gap-4">
            {/* Project Filter */}
            <div className="flex items-center gap-2">
              <Label className="text-sm text-stone-600 whitespace-nowrap">Project:</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger className="w-[180px]" data-testid="project-filter">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* iter-288: BOQ Filter (only visible when a specific project is chosen) */}
            {selectedProject !== 'all' && boqWorksheets.length > 0 && (
              <div className="flex items-center gap-2" data-testid="boq-filter-wrapper">
                <Label className="text-sm text-stone-600 whitespace-nowrap">BOQ:</Label>
                <Select value={selectedBoqWorksheet} onValueChange={setSelectedBoqWorksheet}>
                  <SelectTrigger className="w-[220px]" data-testid="boq-filter">
                    <SelectValue placeholder="All BOQs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All BOQs</SelectItem>
                    {boqWorksheets.map(ws => (
                      <SelectItem key={ws.id} value={ws.id} data-testid={`boq-filter-option-${ws.id}`}>
                        {ws.worksheet_number || ws.name || ws.id.slice(0, 8)}
                        {ws.name && ws.worksheet_number ? ` · ${ws.name}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* View Mode */}
            <div className="flex items-center gap-2">
              <Label className="text-sm text-stone-600 whitespace-nowrap">View:</Label>
              <div className="flex gap-1">
                {['Day', 'Week', 'Month', 'Year'].map((mode) => (
                  <Button
                    key={mode}
                    variant={viewMode === mode ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => changeViewMode(mode)}
                    className={viewMode === mode ? 'bg-red-600 text-white' : ''}
                    data-testid={`view-mode-${mode.toLowerCase()}`}
                  >
                    {mode}
                  </Button>
                ))}
              </div>
            </div>

            {/* Sort By Dropdown */}
            <div className="flex items-center gap-2">
              <Label className="text-sm text-stone-600 whitespace-nowrap">
                <ArrowUpDown size={14} className="inline mr-1" />
                Sort:
              </Label>
              <Select value={sortBy} onValueChange={(val) => { setSortBy(val); }}>
                <SelectTrigger className="w-[160px]" data-testid="sort-by-filter">
                  <SelectValue placeholder="Default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="start_date">By Start Date</SelectItem>
                  <SelectItem value="dependency">By Dependency Chain</SelectItem>
                  <SelectItem value="category">By Category</SelectItem>
                  <SelectItem value="vendor">By Vendor</SelectItem>
                  {taskOrder.length > 0 && (
                    <SelectItem value="custom">Custom Order</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Reorder Mode Button (Admin Only) */}
            {isAdmin && (
              <Button
                variant={showReorderMode ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowReorderMode(!showReorderMode)}
                className={showReorderMode ? 'bg-indigo-600 text-white' : ''}
                data-testid="reorder-mode-btn"
              >
                <GripVertical size={16} className="mr-1" />
                {showReorderMode ? 'Exit Reorder' : 'Reorder Tasks'}
              </Button>
            )}

            {/* Read-only badge for Vendors/Clients */}
            {isClient && (
              <Badge variant="outline" className="text-stone-500 border-stone-300">
                <Eye size={14} className="mr-1" />
                Read-only View
              </Badge>
            )}
            
            {/* Quick Create Task Button */}
            {(isAdmin || user?.role === 'vendor') && (
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  fetchMainCategories();
                  setQuickCreateData(prev => ({
                    ...prev,
                    project_id: selectedProject !== 'all' ? selectedProject : '',
                    main_category_id: '',
                    sub_category_id: '',
                  }));
                  setSubCategories([]);
                  setMainCatSearch('');
                  setSubCatSearch('');
                  setQuickCreateOpen(true);
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white ml-auto"
                data-testid="quick-create-task-btn"
              >
                <Plus size={16} className="mr-1" />
                Quick Create Task
              </Button>
            )}
          </div>
          
          {/* Admin Controls - Second Row */}
          {isAdmin && (
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-stone-100">
              {dependencyMode ? (
                <div className="flex items-center gap-3 w-full bg-purple-50 p-2 rounded-lg border border-purple-200">
                  <Badge className="bg-purple-600 text-white animate-pulse">
                    <Link2 size={14} className="mr-1" />
                    Dependency Mode Active
                  </Badge>
                  <span className="text-sm text-purple-700">
                    {dependencySource 
                      ? `Click on the predecessor task for "${tasks.find(t => t.id === dependencySource)?.title || 'selected task'}"`
                      : 'Click on a task to start'}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={cancelDependencyMode}
                    className="ml-auto border-purple-300 text-purple-700 hover:bg-purple-100"
                  >
                    <X size={16} className="mr-1" />
                    Cancel
                  </Button>
                </div>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDependencyManager(true)}
                    data-testid="manage-dependencies-btn"
                  >
                    <Settings size={16} className="mr-2" />
                    Manage
                  </Button>
                  
                  {/* AI Arrange - Full Task Arrangement (Dates, Order, Categories, Dependencies) */}
                  <Button
                    size="sm"
                    onClick={fetchAIArrangements}
                    disabled={loadingArrange || selectedProject === 'all'}
                    className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                    data-testid="ai-arrange-btn"
                    title={selectedProject === 'all' ? 'Select a project first' : 'Arrange tasks, dates, and dependencies'}
                  >
                    {loadingArrange ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                        Arranging...
                      </>
                    ) : (
                      <>
                        <Sparkles size={16} className="mr-2" />
                        AI Arrange
                      </>
                    )}
                  </Button>
                  
                  {/* AI Pro - Project Timeline Prediction */}
                  <Button
                    size="sm"
                    onClick={fetchAiProPrediction}
                    disabled={loadingAiPro || selectedProject === 'all'}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                    data-testid="ai-pro-btn"
                    title={selectedProject === 'all' ? 'Select a project first' : 'Predict project timeline'}
                  >
                    {loadingAiPro ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                        Predicting...
                      </>
                    ) : (
                      <>
                        <Clock size={16} className="mr-2" />
                        AI Pro
                      </>
                    )}
                  </Button>

                  {/* iter-338k: Share project Gantt — email + public link */}
                  <Button
                    size="sm"
                    onClick={() => setShareDialogOpen(true)}
                    disabled={selectedProject === 'all'}
                    className="bg-blue-700 hover:bg-blue-800 text-white"
                    data-testid="gantt-share-btn"
                    title={selectedProject === 'all' ? 'Select a project first' : 'Share Gantt via email or public link'}
                  >
                    <Share2 size={16} className="mr-2" />
                    Share Gantt
                  </Button>
                  
                  {/* Project Checklist Export */}
                  {selectedProject !== 'all' && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-green-200 text-green-700 hover:bg-green-50"
                          data-testid="checklist-export-btn"
                        >
                          <ClipboardCheck size={16} className="mr-2" />
                          Checklist
                          <ChevronRight size={14} className="ml-1 rotate-90" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleExportChecklist('pdf')}
                          className="cursor-pointer"
                        >
                          <Download size={14} className="mr-2 text-red-500" />
                          Export as PDF
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleExportChecklist('excel')}
                          className="cursor-pointer"
                        >
                          <FileSpreadsheet size={14} className="mr-2 text-green-600" />
                          Export as Excel
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* P2: AI Suggestions Panel */}
        {showAISuggestions && aiSuggestions.length > 0 && (
          <div className="apple-card p-4 border-l-4 border-purple-500 bg-gradient-to-r from-purple-50 to-indigo-50" data-testid="ai-suggestions-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg">
                  <Sparkles size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900">AI-Suggested Dependencies</h3>
                  <p className="text-sm text-stone-500">{aiSuggestions.length} suggestions to review</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAISuggestions(false)}
              >
                <X size={16} />
              </Button>
            </div>
            
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {aiSuggestions.map((suggestion, idx) => (
                <div 
                  key={`${suggestion.predecessor_task_id}-${suggestion.successor_task_id}`}
                  className="flex items-start gap-4 p-3 bg-white rounded-lg border border-purple-100 hover:border-purple-200 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-stone-900 truncate">{suggestion.predecessor_title}</span>
                      <ChevronRight size={16} className="text-purple-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-stone-900 truncate">{suggestion.successor_title}</span>
                    </div>
                    <p className="text-xs text-stone-500">{suggestion.reason}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex items-center gap-1">
                        <div className={`w-2 h-2 rounded-full ${
                          suggestion.confidence >= 0.8 ? 'bg-green-500' :
                          suggestion.confidence >= 0.6 ? 'bg-yellow-500' : 'bg-orange-500'
                        }`} />
                        <span className="text-xs text-stone-400">{Math.round(suggestion.confidence * 100)}% confidence</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-green-600 border-green-200 hover:bg-green-50"
                      onClick={() => applyAISuggestion(suggestion)}
                    >
                      <Check size={14} className="mr-1" />
                      Apply
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-stone-400 hover:text-red-500"
                      onClick={() => dismissAISuggestion(suggestion, idx)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end mt-3 pt-3 border-t border-purple-100">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  aiSuggestions.forEach(s => applyAISuggestion(s));
                }}
                className="text-purple-600 border-purple-200 hover:bg-purple-50"
              >
                Apply All ({aiSuggestions.length})
              </Button>
            </div>
          </div>
        )}

        {/* AI Pro Prediction Panel */}
        {showAiProPanel && aiProPrediction && (
          <div className={`apple-card p-4 border-l-4 ${
            aiProPrediction.timeline_status === 'ahead' ? 'border-green-500 bg-gradient-to-r from-green-50 to-emerald-50' :
            aiProPrediction.timeline_status === 'behind' ? 'border-red-500 bg-gradient-to-r from-red-50 to-orange-50' :
            'border-amber-500 bg-gradient-to-r from-amber-50 to-yellow-50'
          }`} data-testid="ai-pro-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  aiProPrediction.timeline_status === 'ahead' ? 'bg-gradient-to-br from-green-500 to-emerald-500' :
                  aiProPrediction.timeline_status === 'behind' ? 'bg-gradient-to-br from-red-500 to-orange-500' :
                  'bg-gradient-to-br from-amber-500 to-yellow-500'
                }`}>
                  <Clock size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900">AI Pro Timeline Analysis</h3>
                  <p className="text-sm text-stone-500">{aiProPrediction.project_name}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowAiProPanel(false)}>
                <X size={16} />
              </Button>
            </div>
            
            {/* Main Status Banner */}
            <div className={`p-4 rounded-lg mb-4 ${
              aiProPrediction.timeline_status === 'ahead' ? 'bg-green-100 border border-green-200' :
              aiProPrediction.timeline_status === 'behind' ? 'bg-red-100 border border-red-200' :
              'bg-amber-100 border border-amber-200'
            }`}>
              <div className="flex items-center gap-3">
                {aiProPrediction.timeline_status === 'ahead' && <CheckCircle2 size={24} className="text-green-600" />}
                {aiProPrediction.timeline_status === 'behind' && <AlertTriangle size={24} className="text-red-600" />}
                {aiProPrediction.timeline_status === 'on_track' && <Clock size={24} className="text-amber-600" />}
                <div>
                  <p className={`text-lg font-bold ${
                    aiProPrediction.timeline_status === 'ahead' ? 'text-green-800' :
                    aiProPrediction.timeline_status === 'behind' ? 'text-red-800' :
                    'text-amber-800'
                  }`}>
                    {aiProPrediction.timeline_status === 'ahead' 
                      ? `Project Running ${aiProPrediction.days_difference} Days Ahead of Schedule` 
                      : aiProPrediction.timeline_status === 'behind'
                      ? `Project Running ${aiProPrediction.days_difference} Days Behind Schedule`
                      : 'Project is On Track'}
                  </p>
                  <p className="text-sm opacity-75">
                    {aiProPrediction.completion_percentage}% complete • Expected: {aiProPrediction.expected_progress}% by now
                  </p>
                </div>
              </div>
            </div>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-4 gap-3 mb-4">
              <div className="bg-white p-3 rounded-lg border">
                <p className="text-xs text-stone-500">Total Tasks</p>
                <p className="text-xl font-bold text-stone-800">{aiProPrediction.total_tasks}</p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-green-200">
                <p className="text-xs text-stone-500">Completed</p>
                <p className="text-xl font-bold text-green-600">{aiProPrediction.completed_tasks}</p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-blue-200">
                <p className="text-xs text-stone-500">In Progress</p>
                <p className="text-xl font-bold text-blue-600">{aiProPrediction.in_progress_tasks}</p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-red-200">
                <p className="text-xs text-stone-500">Overdue</p>
                <p className="text-xl font-bold text-red-600">{aiProPrediction.overdue_tasks}</p>
              </div>
            </div>
            
            {/* Timeline Info */}
            <div className="bg-white p-3 rounded-lg border mb-4">
              <div className="flex justify-between text-sm">
                <div>
                  <p className="text-stone-500">Project Dates</p>
                  <p className="font-medium">{aiProPrediction.project_start} → {aiProPrediction.project_end}</p>
                </div>
                <div className="text-right">
                  <p className="text-stone-500">Days Remaining</p>
                  <p className={`font-bold ${aiProPrediction.days_remaining < 7 ? 'text-red-600' : 'text-stone-800'}`}>
                    {aiProPrediction.days_remaining} days
                  </p>
                </div>
              </div>
              {aiProPrediction.predicted_end_date && (
                <div className="mt-2 pt-2 border-t">
                  <p className="text-xs text-stone-500">Predicted Completion</p>
                  <p className="font-medium text-stone-800">{aiProPrediction.predicted_end_date}</p>
                </div>
              )}
            </div>
            
            {/* Risk Factors */}
            {aiProPrediction.risk_factors.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-stone-700 mb-2">Risk Factors</p>
                <div className="space-y-1">
                  {aiProPrediction.risk_factors.map((risk, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-red-700 bg-red-50 p-2 rounded">
                      <AlertCircle size={14} />
                      {risk}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Recommendations */}
            {aiProPrediction.recommendations.length > 0 && (
              <div>
                <p className="text-sm font-medium text-stone-700 mb-2">Recommendations</p>
                <div className="space-y-1">
                  {aiProPrediction.recommendations.map((rec, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 p-2 rounded">
                      <Info size={14} />
                      {rec}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* AI Task Arrangement Panel */}
        {showArrangePanel && aiArrangements && (
          <div className="apple-card p-4 border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50" data-testid="ai-arrange-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg">
                  <Sparkles size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900">AI Task Arrangement</h3>
                  <p className="text-sm text-stone-500">
                    {aiArrangements.message}
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowArrangePanel(false)}>
                <X size={16} />
              </Button>
            </div>
            
            {/* Arrangement Summary */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-white p-3 rounded-lg border border-emerald-200">
                <p className="text-xs text-stone-500">Total Tasks</p>
                <p className="text-2xl font-bold text-stone-800">{aiArrangements.total_tasks}</p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-blue-200">
                <p className="text-xs text-stone-500">Arrangements</p>
                <p className="text-2xl font-bold text-blue-600">{aiArrangements.arrangements?.length || 0}</p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-purple-200">
                <p className="text-xs text-stone-500">Dependencies</p>
                <p className="text-2xl font-bold text-purple-600">{aiArrangements.dependencies?.length || 0}</p>
              </div>
            </div>
            
            {/* Arrangement Details */}
            {aiArrangements.arrangements?.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-stone-700 mb-2">Task Date Adjustments</p>
                <div className="max-h-[200px] overflow-y-auto bg-white rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2 font-medium">Order</th>
                        <th className="text-left p-2 font-medium">Task</th>
                        <th className="text-left p-2 font-medium">Category</th>
                        <th className="text-left p-2 font-medium">New Dates</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {aiArrangements.arrangements.map((arr, idx) => {
                        const task = tasks.find(t => t.id === arr.task_id);
                        return (
                          <tr key={idx} className="hover:bg-stone-50">
                            <td className="p-2">
                              <span className="inline-flex items-center justify-center w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold">
                                {arr.new_order}
                              </span>
                            </td>
                            <td className="p-2">
                              <span className="font-medium">{task?.title || arr.task_id}</span>
                            </td>
                            <td className="p-2">
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                {arr.category_group || task?.category_group || '-'}
                              </span>
                            </td>
                            <td className="p-2 text-xs text-stone-600">
                              {arr.new_start_date} → {arr.new_end_date}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Dependencies to create */}
            {aiArrangements.dependencies?.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-stone-700 mb-2">Dependencies to Create</p>
                <div className="space-y-2 max-h-[150px] overflow-y-auto">
                  {aiArrangements.dependencies.map((dep, idx) => {
                    const pred = tasks.find(t => t.id === dep.predecessor_id);
                    const succ = tasks.find(t => t.id === dep.successor_id);
                    return (
                      <div key={idx} className="flex items-center gap-2 text-sm bg-white p-2 rounded border">
                        <span className="font-medium text-stone-800">{pred?.title || dep.predecessor_id}</span>
                        <ArrowRight size={14} className="text-purple-500" />
                        <span className="font-medium text-stone-800">{succ?.title || dep.successor_id}</span>
                        <span className="text-xs text-stone-500 ml-auto">{dep.reason}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Apply Button */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowArrangePanel(false)}>
                Cancel
              </Button>
              <Button 
                onClick={applyAIArrangements}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
              >
                <Check size={16} className="mr-2" />
                Apply All ({(aiArrangements.arrangements?.length || 0) + (aiArrangements.dependencies?.length || 0)})
              </Button>
            </div>
          </div>
        )}

        {/* Reorder Mode Panel */}
        {showReorderMode && isAdmin && (
          <div className="apple-card p-4 border-2 border-indigo-200 bg-indigo-50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg">
                  <GripVertical size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900">Reorder Tasks</h3>
                  <p className="text-sm text-stone-500">Drag tasks to reorder display. This is visual only - dependencies are preserved.</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={saveTaskOrder}
                  className="text-indigo-600 border-indigo-200 hover:bg-indigo-100"
                >
                  <Save size={14} className="mr-1" />
                  Save Order
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowReorderMode(false)}
                >
                  <X size={16} />
                </Button>
              </div>
            </div>
            
            <div className="bg-white rounded-lg border border-indigo-100 max-h-[400px] overflow-y-auto">
              {getSortedTasks().map((task, idx) => {
                const predecessor = task.depends_on_task_id ? tasks.find(t => t.id === task.depends_on_task_id) : null;
                
                return (
                  <div
                    key={task.id}
                    draggable
                    onDragStart={(e) => handleReorderDragStart(e, task.id)}
                    onDragOver={(e) => handleReorderDragOver(e, task.id)}
                    onDrop={(e) => handleReorderDrop(e, task.id)}
                    className={`flex items-center gap-3 p-3 border-b border-stone-100 cursor-move hover:bg-indigo-50 transition-colors ${
                      draggedTaskId === task.id ? 'opacity-50 bg-indigo-100' : ''
                    }`}
                  >
                    <div className="flex-shrink-0 text-stone-400 hover:text-stone-600">
                      <GripVertical size={18} />
                    </div>
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-sm font-medium text-stone-500">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-900 truncate">{task.title}</p>
                      <div className="flex items-center gap-2 text-xs text-stone-500">
                        <span>{task.category_name || task.category_code || 'No category'}</span>
                        {predecessor && (
                          <>
                            <span>•</span>
                            <span className="text-purple-600">
                              <Link2 size={10} className="inline mr-1" />
                              Depends on: {predecessor.title}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <Badge 
                      variant="outline" 
                      className={`flex-shrink-0 ${
                        task.status === 'completed' ? 'text-green-600 border-green-200' :
                        task.status === 'completed_with_snags' ? 'text-sky-600 border-sky-200' :
                        task.status === 'in_progress' ? 'text-blue-600 border-blue-200' :
                        task.status === 'delayed' ? 'text-red-600 border-red-200' :
                        'text-stone-500 border-stone-200'
                      }`}
                    >
                      {task.status?.replace(/_/g, ' ') || 'Not Started'}
                    </Badge>
                    <Select
                      value={task.status || 'not_started'}
                      onValueChange={async (newStatus) => {
                        try {
                          await axios.put(`${API}/tasks/${task.id}`, { status: newStatus });
                          setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
                          toast.success(`Task "${task.name || task.title}" status updated to ${newStatus.replace(/_/g, ' ')}`);
                          // Re-render gantt to reflect color change
                          if (ganttInstance.current && ganttRef.current) {
                            setTimeout(() => {
                              const bar = ganttRef.current.querySelector(`[data-id="${task.id}"]`);
                              if (bar) {
                                bar.className = bar.className.replace(/gantt-task-\w+/g, `gantt-task-${newStatus}`);
                                const rect = bar.querySelector('.bar');
                                if (rect) rect.style.fill = (statusColors[newStatus] || statusColors.not_started).fill;
                              }
                            }, 100);
                          }
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to update status');
                        }
                      }}
                    >
                      <SelectTrigger className="h-7 w-[140px] text-xs" data-testid={`gantt-status-${task.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_started">Not Started</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="completed_with_snags">Completed with Snags</SelectItem>
                        <SelectItem value="delayed">Delayed</SelectItem>
                        <SelectItem value="awaiting_client_approval">Awaiting Approval</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-3 flex items-center gap-2 text-xs text-stone-500">
              <Info size={12} />
              <span>Reordering affects visual display only. Dependencies and scheduling remain unchanged.</span>
            </div>
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="apple-card p-3 text-center">
            <p className="text-2xl font-bold text-stone-900">{taskCounts.total}</p>
            <p className="text-xs text-stone-500">Total Tasks</p>
          </div>
          <div className="apple-card p-3 text-center border-l-4 border-green-500">
            <p className="text-2xl font-bold text-green-600">{taskCounts.completed}</p>
            <p className="text-xs text-stone-500">Completed</p>
          </div>
          <div className="apple-card p-3 text-center border-l-4 border-blue-500">
            <p className="text-2xl font-bold text-blue-600">{taskCounts.inProgress}</p>
            <p className="text-xs text-stone-500">In Progress</p>
          </div>
          <div className="apple-card p-3 text-center border-l-4 border-gray-400">
            <p className="text-2xl font-bold text-gray-600">{taskCounts.notStarted}</p>
            <p className="text-xs text-stone-500">Not Started</p>
          </div>
          <div className="apple-card p-3 text-center border-l-4 border-purple-500">
            <p className="text-2xl font-bold text-purple-600">{taskCounts.withDependencies}</p>
            <p className="text-xs text-stone-500">With Dependencies</p>
          </div>
        </div>

        {/* Legend */}
        <div className="apple-card p-3 overflow-hidden">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            <span className="text-stone-600 font-medium">Legend:</span>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded flex-shrink-0" style={{ backgroundColor: statusColors.not_started.fill }}></div>
              <span className="whitespace-nowrap">Not Started</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded flex-shrink-0" style={{ backgroundColor: statusColors.in_progress.fill }}></div>
              <span className="whitespace-nowrap">In Progress</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded flex-shrink-0" style={{ backgroundColor: statusColors.completed.fill }}></div>
              <span className="whitespace-nowrap">Completed</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded flex-shrink-0" style={{ backgroundColor: statusColors.completed_with_snags.fill }}></div>
              <span className="whitespace-nowrap">Completed with Snags</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded flex-shrink-0" style={{ backgroundColor: statusColors.delayed.fill }}></div>
              <span className="whitespace-nowrap">Delayed</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded flex-shrink-0" style={{ backgroundColor: statusColors.awaiting_client_approval.fill }}></div>
              <span className="whitespace-nowrap">Awaiting Approval</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-0.5 bg-indigo-500 flex-shrink-0"></div>
              <span className="whitespace-nowrap">Dependency Arrow</span>
            </div>
          </div>
        </div>

        {/* Category Legend — Clickable MULTI-select filter (iter-286) */}
        {uniqueCategories.length > 0 && (
          <div className="apple-card p-3" data-testid="category-legend">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-stone-600 font-medium mr-1">Categories:</span>
              {uniqueCategories.map((cat, i) => {
                const catKey = cat.name;
                const isActive = ganttCategoryFilter.has(catKey);
                const color = CATEGORY_COLORS[i % CATEGORY_COLORS.length];
                return (
                  <button
                    key={catKey}
                    onClick={() => setGanttCategoryFilter(prev => {
                      const next = new Set(prev);
                      if (next.has(catKey)) next.delete(catKey); else next.add(catKey);
                      return next;
                    })}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs transition-all cursor-pointer ${
                      isActive ? 'ring-2 ring-offset-1 shadow-sm font-semibold' : 'hover:shadow-sm'
                    }`}
                    style={{ borderColor: color, backgroundColor: isActive ? color + '30' : color + '10' }}
                    data-testid={`gantt-legend-${catKey}`}
                  >
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} />
                    <span className="whitespace-nowrap text-stone-700">
                      {cat.code ? `${cat.code} - ` : ''}{cat.name}
                    </span>
                    <span className="text-stone-400">({cat.count})</span>
                    {isActive && <span className="text-[10px] ml-0.5">✓</span>}
                  </button>
                );
              })}
              {ganttCategoryFilter.size > 0 && (
                <>
                  <span className="text-xs text-stone-600 ml-1" data-testid="gantt-legend-count">{ganttCategoryFilter.size} selected</span>
                  <button onClick={() => setGanttCategoryFilter(new Set())} className="text-xs text-stone-500 hover:text-stone-700 underline" data-testid="gantt-legend-clear">
                    Clear
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Gantt Chart Container — gantt-container handles horizontal scrolling */}
        <div className="apple-card p-0" style={{ maxWidth: '100%' }}>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
              <span className="ml-3 text-stone-500">Loading tasks...</span>
            </div>
          ) : (
            <div 
              ref={ganttRef} 
              className={`min-h-[400px] ${dependencyMode ? 'dependency-mode' : ''} ${!isAdmin ? 'gantt-readonly' : ''}`}
              style={{ width: '100%' }}
              data-testid="gantt-chart-container"
            />
          )}
        </div>

        {/* Tips - Different for Admin vs Vendor/Client */}
        {isAdmin ? (
          <div className="apple-card p-4 bg-blue-50 border-blue-200">
            <div className="flex items-start gap-3">
              <Info size={20} className="text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">Admin Tips</h4>
                <ul className="text-sm text-blue-700 mt-1 space-y-1">
                  <li>• <strong>Drag task bars</strong> horizontally to reschedule start/end dates</li>
                  <li>• <strong>Drag progress handle</strong> (right side of bar) to update task progress</li>
                  <li>• <strong>Click "Add Dependencies"</strong> then click two tasks to create a Finish-to-Start dependency</li>
                  <li>• <strong>Use "Reorder Tasks"</strong> to visually group similar tasks together</li>
                  <li>• <strong>Sort by Dependency Chain</strong> to see tasks in logical execution order</li>
                </ul>
              </div>
            </div>
          </div>
        ) : (
          <div className="apple-card p-4 bg-stone-50 border-stone-200">
            <div className="flex items-start gap-3">
              <Eye size={20} className="text-stone-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-stone-900">
                  {isVendor ? 'Vendor View' : 'Client View'}
                </h4>
                <ul className="text-sm text-stone-600 mt-1 space-y-1">
                  <li>• <strong>View timeline</strong> of {isVendor ? 'your assigned tasks' : 'your project tasks'}</li>
                  <li>• <strong>Dependency arrows</strong> show task relationships and sequencing</li>
                  <li>• <strong>Click any task</strong> to view details</li>
                  <li>• <strong>Use Sort by</strong> dropdown to organize the view</li>
                  {isVendor && <li>• <strong>Contact admin</strong> to request schedule changes</li>}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Quick Create Task Dialog */}
        <Dialog open={quickCreateOpen} onOpenChange={setQuickCreateOpen}>
          <DialogContent className="sm:max-w-md" data-testid="quick-create-task-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus size={20} className="text-emerald-600" />
                Quick Create Task
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm font-medium">Task Title *</Label>
                <Input
                  value={quickCreateData.title}
                  onChange={(e) => setQuickCreateData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter task title"
                  data-testid="quick-create-title"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium">Project *</Label>
                <Select 
                  value={quickCreateData.project_id} 
                  onValueChange={(v) => setQuickCreateData(prev => ({ ...prev, project_id: v }))}
                >
                  <SelectTrigger data-testid="quick-create-project">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium">Main Category *</Label>
                <Select 
                  value={quickCreateData.main_category_id} 
                  onValueChange={(v) => {
                    setQuickCreateData(prev => ({ ...prev, main_category_id: v, sub_category_id: '' }));
                    setSubCatSearch('');
                    fetchSubCategories(v);
                  }}
                >
                  <SelectTrigger data-testid="quick-create-category">
                    <SelectValue placeholder="Select main category" />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="px-2 pb-2">
                      <Input
                        placeholder="Search categories..."
                        value={mainCatSearch}
                        onChange={(e) => setMainCatSearch(e.target.value)}
                        className="h-8 text-sm"
                        data-testid="quick-create-category-search"
                      />
                    </div>
                    {mainCategories
                      .filter(c => !mainCatSearch || `${c.code} ${c.name}`.toLowerCase().includes(mainCatSearch.toLowerCase()))
                      .map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.code} - {c.name}</SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium">Sub Category</Label>
                <Select 
                  value={quickCreateData.sub_category_id} 
                  onValueChange={(v) => setQuickCreateData(prev => ({ ...prev, sub_category_id: v }))}
                  disabled={!quickCreateData.main_category_id}
                >
                  <SelectTrigger data-testid="quick-create-subcategory">
                    <SelectValue placeholder={quickCreateData.main_category_id ? "Select sub-category" : "Select main category first"} />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="px-2 pb-2">
                      <Input
                        placeholder="Search sub-categories..."
                        value={subCatSearch}
                        onChange={(e) => setSubCatSearch(e.target.value)}
                        className="h-8 text-sm"
                        data-testid="quick-create-subcategory-search"
                      />
                    </div>
                    {subCategories
                      .filter(c => !subCatSearch || `${c.code} ${c.name}`.toLowerCase().includes(subCatSearch.toLowerCase()))
                      .map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.code} - {c.name}</SelectItem>
                      ))
                    }
                    {subCategories.length === 0 && quickCreateData.main_category_id && (
                      <div className="px-2 py-2 text-sm text-stone-400">No sub-categories found</div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Start Date</Label>
                  <Input
                    type="date"
                    value={quickCreateData.start_date}
                    onChange={(e) => setQuickCreateData(prev => ({ ...prev, start_date: e.target.value }))}
                    data-testid="quick-create-start"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Deadline</Label>
                  <Input
                    type="date"
                    value={quickCreateData.deadline}
                    onChange={(e) => setQuickCreateData(prev => ({ ...prev, deadline: e.target.value }))}
                    data-testid="quick-create-deadline"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Description (Optional)</Label>
                <Textarea
                  value={quickCreateData.description}
                  onChange={(e) => setQuickCreateData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief task description..."
                  rows={2}
                  data-testid="quick-create-description"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setQuickCreateOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleQuickCreate}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="quick-create-submit"
              >
                Create Task
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dependency Confirmation Dialog */}
        <Dialog open={showDependencyDialog} onOpenChange={setShowDependencyDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Dependency</DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              {/* P1: Dependency Type Selector */}
              <div className="mb-4">
                <Label className="text-sm font-medium text-stone-700 mb-2 block">Dependency Type</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'FS', label: 'Finish-to-Start', desc: 'Successor waits for predecessor to finish' },
                    { value: 'SS', label: 'Start-to-Start', desc: 'Both tasks can start together' },
                    { value: 'SF', label: 'Start-to-Finish', desc: 'Successor can\'t finish until predecessor starts' },
                    { value: 'FF', label: 'Finish-to-Finish', desc: 'Both tasks must finish together' },
                  ].map(type => (
                    <button
                      key={type.value}
                      onClick={() => setSelectedDependencyType(type.value)}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        selectedDependencyType === type.value 
                          ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200' 
                          : 'border-stone-200 hover:border-purple-300 hover:bg-stone-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`text-xs ${
                          selectedDependencyType === type.value ? 'bg-purple-100 text-purple-700 border-purple-300' : ''
                        }`}>
                          {type.value}
                        </Badge>
                        <span className="text-sm font-medium">{type.label}</span>
                      </div>
                      <p className="text-xs text-stone-500 mt-1">{type.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              
              <p className="text-stone-600 mb-4">
                You're about to create a <strong>{
                  selectedDependencyType === 'FS' ? 'Finish-to-Start' :
                  selectedDependencyType === 'SS' ? 'Start-to-Start' :
                  selectedDependencyType === 'SF' ? 'Start-to-Finish' : 'Finish-to-Finish'
                }</strong> dependency:
              </p>
              
              <div className="flex items-center gap-3 p-4 bg-stone-50 rounded-lg">
                <div className="flex-1 text-center">
                  <p className="text-sm text-stone-500">Predecessor</p>
                  <p className="font-semibold text-stone-900">
                    {tasks.find(t => t.id === pendingDependency.to)?.title || 'Unknown'}
                  </p>
                </div>
                
                <div className="text-purple-600 flex flex-col items-center">
                  <Badge variant="outline" className="text-xs mb-1">{selectedDependencyType}</Badge>
                  <ChevronRight size={24} />
                </div>
                
                <div className="flex-1 text-center">
                  <p className="text-sm text-stone-500">Dependent</p>
                  <p className="font-semibold text-stone-900">
                    {tasks.find(t => t.id === pendingDependency.from)?.title || 'Unknown'}
                  </p>
                </div>
              </div>
              
              <p className="text-sm text-stone-500 mt-4">
                <AlertTriangle size={14} className="inline mr-1" />
                {selectedDependencyType === 'FS' && 'Dependent task cannot start until predecessor finishes.'}
                {selectedDependencyType === 'SS' && 'Dependent task cannot start before predecessor starts.'}
                {selectedDependencyType === 'SF' && 'Dependent task cannot finish until predecessor starts.'}
                {selectedDependencyType === 'FF' && 'Dependent task cannot finish before predecessor finishes.'}
              </p>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={cancelDependencyMode}>
                Cancel
              </Button>
              <Button onClick={confirmDependency} className="bg-purple-600 hover:bg-purple-700">
                <Link2 size={16} className="mr-2" />
                Create Dependency
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dependency Manager Dialog - SIMPLIFIED */}
        <Dialog open={showDependencyManager} onOpenChange={setShowDependencyManager}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Link2 size={20} className="text-purple-600" />
                Manage Task Dependencies
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Existing Dependencies Section */}
              {tasksWithDependencies.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-stone-700 flex items-center gap-2">
                    <span className="bg-purple-100 px-2 py-0.5 rounded text-purple-700 text-xs">
                      {tasksWithDependencies.length}
                    </span>
                    Existing Dependencies
                  </h3>
                  <div className="space-y-2 max-h-[250px] overflow-y-auto">
                    {tasksWithDependencies.map(task => {
                      const predecessor = tasks.find(t => t.id === task.depends_on_task_id);
                      const isEditing = editingDependency?.taskId === task.id;
                      
                      return (
                        <div 
                          key={task.id}
                          className={`p-3 rounded-lg border ${isEditing ? 'bg-blue-50 border-blue-200' : 'bg-purple-50 border-purple-100'}`}
                        >
                          {isEditing ? (
                            // Editing mode
                            <div className="space-y-3">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-stone-500">Editing dependency for:</span>
                                <span className="text-sm font-medium text-stone-900">{task.title}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Label className="text-xs text-stone-500 whitespace-nowrap">New Predecessor:</Label>
                                <Select
                                  value={editingDependency.newPredecessorId || ''}
                                  onValueChange={(val) => setEditingDependency({
                                    ...editingDependency,
                                    newPredecessorId: val
                                  })}
                                >
                                  <SelectTrigger className="flex-1">
                                    <SelectValue placeholder="Select new predecessor..." />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-[200px]">
                                    {getValidPredecessors(task.id).map(t => (
                                      <SelectItem key={t.id} value={t.id}>
                                        {t.title}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="flex justify-end gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={cancelEditDependency}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700 text-white"
                                  onClick={() => updateDependency(task.id, editingDependency.newPredecessorId)}
                                  disabled={!editingDependency.newPredecessorId || editingDependency.newPredecessorId === editingDependency.currentPredecessorId}
                                >
                                  <Check size={14} className="mr-1" />
                                  Save
                                </Button>
                              </div>
                            </div>
                          ) : (
                            // View mode
                            <div className="flex items-center gap-3">
                              <div className="flex-1 flex items-center gap-2 min-w-0">
                                <span className="text-sm font-medium text-stone-700 truncate">
                                  {predecessor?.title || 'Unknown'}
                                </span>
                                <ArrowRight size={16} className="text-purple-500 flex-shrink-0" />
                                <span className="text-sm font-medium text-stone-900 truncate">
                                  {task.title}
                                </span>
                              </div>
                              <div className="flex gap-1 flex-shrink-0">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                  onClick={() => startEditDependency(task)}
                                  title="Edit dependency"
                                >
                                  <Pencil size={14} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => removeDependency(task.id)}
                                  title="Remove dependency"
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Add New Dependency Section */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-stone-700 mb-3">Add New Dependency</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Task Selection */}
                  <div>
                    <Label className="text-xs text-stone-500 mb-1 block">Task that waits (Successor)</Label>
                    <Select 
                      value={selectedTaskForDep?.id || ''} 
                      onValueChange={(val) => setSelectedTaskForDep(tasks.find(t => t.id === val))}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a task..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        {tasks.filter(t => !t.depends_on_task_id).map(task => (
                          <SelectItem key={task.id} value={task.id}>
                            {task.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Predecessor Selection */}
                  <div>
                    <Label className="text-xs text-stone-500 mb-1 block">Must finish first (Predecessor)</Label>
                    <Select 
                      onValueChange={(val) => {
                        if (selectedTaskForDep && val) {
                          addDependencyFromDropdown(selectedTaskForDep.id, val);
                        }
                      }}
                      disabled={!selectedTaskForDep}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={selectedTaskForDep ? "Select predecessor..." : "First select a task"} />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        {selectedTaskForDep && getValidPredecessors(selectedTaskForDep.id).map(task => (
                          <SelectItem key={task.id} value={task.id}>
                            {task.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Quick Tips */}
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 text-sm">
                <p className="text-blue-700">
                  <Info size={14} className="inline mr-1" />
                  <strong>Finish-to-Start:</strong> The predecessor must complete before the successor can begin.
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDependencyManager(false)}>
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Drag Validation / Cascade Confirmation Dialog */}
        <Dialog open={showDragValidation} onOpenChange={(open) => {
          if (!open) cancelDragOperation();
        }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle size={20} />
                Dependency Conflict Detected
              </DialogTitle>
              <DialogDescription>
                Rescheduling "{pendingDrag?.taskTitle}" affects dependent tasks
              </DialogDescription>
            </DialogHeader>
            
            {dragValidation && (
              <div className="space-y-4 py-4">
                {/* Errors (Blocking) */}
                {dragValidation.errors?.length > 0 && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <ul className="list-disc pl-4 space-y-1">
                        {dragValidation.errors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Warnings (Non-blocking) */}
                {dragValidation.warnings?.length > 0 && (
                  <Alert className="border-amber-200 bg-amber-50">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800">
                      <ul className="list-disc pl-4 space-y-1">
                        {dragValidation.warnings.map((warn, i) => (
                          <li key={i}>{warn}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Affected Tasks */}
                {dragValidation.affected_tasks?.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-stone-700 flex items-center gap-2">
                        <RefreshCw size={16} className="text-blue-600" />
                        Tasks to be auto-shifted ({dragValidation.affected_tasks.length})
                      </h4>
                      <Badge variant="outline" className="text-blue-600 border-blue-200">
                        Cascade Update
                      </Badge>
                    </div>
                    
                    {/* P0 Enhancement: Visual Timeline Preview */}
                    <div className="border rounded-lg p-4 bg-gradient-to-r from-stone-50 to-blue-50">
                      <div className="flex items-center gap-2 mb-3">
                        <Eye size={14} className="text-blue-600" />
                        <span className="text-xs font-semibold text-stone-600 uppercase tracking-wide">Timeline Preview</span>
                        <span className="text-xs text-stone-400">(Ghost bars shown on chart)</span>
                      </div>
                      
                      <div className="space-y-2">
                        {/* Main dragged task */}
                        <div className="flex items-center gap-2">
                          <div className="w-20 text-xs text-stone-500 truncate" title={pendingDrag?.taskTitle}>
                            {pendingDrag?.taskTitle?.substring(0, 12)}...
                          </div>
                          <div className="flex-1 h-6 bg-stone-200 rounded relative overflow-hidden">
                            <div 
                              className="absolute h-full bg-blue-500 rounded opacity-80 flex items-center justify-center"
                              style={{ width: '40%', left: '20%' }}
                            >
                              <span className="text-[10px] text-white font-medium">New Position</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Affected tasks preview */}
                        {dragValidation.affected_tasks.slice(0, 4).map((task, i) => (
                          <div key={task.id} className="flex items-center gap-2">
                            <div className="w-20 text-xs text-stone-500 truncate" title={task.title}>
                              {task.title?.substring(0, 12)}...
                            </div>
                            <div className="flex-1 h-5 bg-stone-200 rounded relative overflow-hidden">
                              {/* Current position (faded) */}
                              <div 
                                className="absolute h-full bg-stone-400 rounded opacity-30"
                                style={{ width: '30%', left: `${20 + i * 5}%` }}
                              />
                              {/* New position (highlighted) */}
                              <div 
                                className="absolute h-full rounded border-2 border-dashed border-indigo-500 bg-indigo-100"
                                style={{ width: '30%', left: `${30 + i * 5 + task.shift_days * 2}%` }}
                              >
                                <span className="absolute -top-4 right-0 text-[9px] text-indigo-600 font-semibold">
                                  +{task.shift_days}d
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {dragValidation.affected_tasks.length > 4 && (
                          <div className="text-xs text-stone-400 text-center">
                            +{dragValidation.affected_tasks.length - 4} more task(s)
                          </div>
                        )}
                      </div>
                      
                      {/* Legend */}
                      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-stone-200">
                        <div className="flex items-center gap-1">
                          <div className="w-4 h-3 bg-stone-400 rounded opacity-30" />
                          <span className="text-[10px] text-stone-500">Current</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-4 h-3 border-2 border-dashed border-indigo-500 bg-indigo-100 rounded" />
                          <span className="text-[10px] text-stone-500">New (After Cascade)</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-stone-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-stone-600">Task</th>
                            <th className="px-3 py-2 text-left font-medium text-stone-600">Current</th>
                            <th className="px-3 py-2 text-left font-medium text-stone-600">New</th>
                            <th className="px-3 py-2 text-center font-medium text-stone-600">Shift</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {dragValidation.affected_tasks.map((task, i) => (
                            <tr key={task.id} className={i % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  {task.status === 'completed' && <Lock size={12} className="text-stone-400" />}
                                  <span className="truncate max-w-[200px]" title={task.title}>
                                    {task.title}
                                  </span>
                                </div>
                              </td>
                              <td className="px-3 py-2 text-stone-500 text-xs">
                                {task.current_start?.split('T')[0]}
                              </td>
                              <td className="px-3 py-2 text-blue-600 text-xs font-medium">
                                {task.new_start?.split('T')[0]}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <Badge variant="outline" className="text-amber-600 border-amber-200 text-xs">
                                  +{task.shift_days} days
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Info about cascade */}
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 text-sm">
                      <p className="text-blue-700">
                        <Info size={14} className="inline mr-1" />
                        <strong>Auto-Cascade:</strong> All dependent tasks will be shifted forward while maintaining their duration and dependency relationships.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter className="flex gap-2 sm:gap-2">
              <Button 
                variant="outline" 
                onClick={cancelDragOperation}
                disabled={applyingCascade}
              >
                Cancel
              </Button>
              
              {dragValidation?.requires_cascade && dragValidation?.affected_tasks?.length > 0 && (
                <>
                  <Button 
                    variant="outline"
                    onClick={applyWithoutCascade}
                    disabled={applyingCascade || !dragValidation?.valid}
                    className="text-amber-600 border-amber-200 hover:bg-amber-50"
                  >
                    {applyingCascade ? (
                      <RefreshCw size={14} className="mr-2 animate-spin" />
                    ) : (
                      <AlertTriangle size={14} className="mr-2" />
                    )}
                    Move Only This Task
                  </Button>
                  
                  <Button 
                    onClick={applyCascadeUpdate}
                    disabled={applyingCascade || !dragValidation?.valid}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {applyingCascade ? (
                      <RefreshCw size={14} className="mr-2 animate-spin" />
                    ) : (
                      <RefreshCw size={14} className="mr-2" />
                    )}
                    Auto-Shift All ({dragValidation?.cascade_count} tasks)
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Date Editor Dialog */}
        <Dialog open={showDateEditor} onOpenChange={setShowDateEditor}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar size={20} className="text-blue-600" />
                Edit Task Dates
              </DialogTitle>
              <DialogDescription>
                {editingTaskDates?.title}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Start Date */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Start Date</Label>
                <input
                  type="date"
                  value={dateEditStart}
                  onChange={(e) => setDateEditStart(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="edit-start-date"
                />
              </div>
              
              {/* End Date (Deadline) */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-red-600">End Date (Deadline)</Label>
                <input
                  type="date"
                  value={dateEditEnd}
                  onChange={(e) => setDateEditEnd(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="edit-end-date"
                />
              </div>
              
              {/* Dependency Warning */}
              {editingTaskDates?.depends_on_task_id && (
                <Alert className="border-amber-200 bg-amber-50">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800 text-sm">
                    This task depends on another task. Start date must be after the predecessor ends.
                  </AlertDescription>
                </Alert>
              )}
              
              {/* Info */}
              <div className="bg-stone-50 p-3 rounded-lg text-sm text-stone-600">
                <Info size={14} className="inline mr-2" />
                Changes will be validated against dependency rules before saving.
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDateEditor(false);
                  setEditingTaskDates(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={saveDateEdits}
                disabled={savingDates || (!dateEditStart && !dateEditEnd)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {savingDates ? (
                  <RefreshCw size={14} className="mr-2 animate-spin" />
                ) : (
                  <Save size={14} className="mr-2" />
                )}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* iter-338k: Gantt share dialog */}
        <GanttShareDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          projectId={selectedProject !== 'all' ? selectedProject : null}
          projectName={projects.find((p) => p.id === selectedProject)?.name}
        />
      </div>
    </Layout>
  );
}
